import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { VUKZ, VUKZ_GRADIENT } from '../theme/vukzTheme';
import { AZURE } from '../services/api';
import PageHeader from '../components/layout/PageHeader';
import useVideoConverter from '../hooks/useVideoConverter';
import { getBestModel, checkMedleyReadiness, needsConversion, ACCEPTED_VIDEO_EXTENSIONS, uploadVideoAsync, uploadVideoToBlob, pollSessionUntilDone, pollLaneScanUntilDone, analyzeSelectedLanes, generateDebugOverlay, checkSessionStatus, getRaceContext, getRaceHeats, setRaceContext, createVideoClip, pollClipUntilReady } from '../services/biomechanicsService';
import VideoTrimmer from '../components/biomechanics/VideoTrimmer';
import { normalizeSession } from '../utils/normalizeSession';
import { getCompetitions, getCompetitionEventsWithPhases } from '../services/swimmingService';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Dot } from 'recharts';
import {
  Upload, Play, Pause, SkipBack, SkipForward, Maximize2, Minimize2,
  Activity, Timer, Waves, Zap, Brain, MessageSquare, Send,
  ThumbsUp, ThumbsDown, ChevronRight, Gauge, TrendingUp,
  RotateCcw, Eye, Box, Sparkles, AlertTriangle, CheckCircle2,
  Ruler, Droplets, Wind, Target, ChevronDown, X, Settings, Scissors,
  BarChart3, Clock, Flame, ArrowRight, Camera, Video, Shield, Cpu, LayoutDashboard, RefreshCw, Link2, Trophy, Layers,
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════════════════
// Constants
// ═══════════════════════════════════════════════════════════════════════════════

const QUICK_PROMPTS = [
  { label: 'Analisar técnica geral', icon: Eye, prompt: 'Faça uma análise técnica geral completa do nado neste vídeo.' },
  { label: 'Avaliar viradas', icon: RotateCcw, prompt: 'Analise a qualidade das viradas: aproximação, giro, impulsão e streamline.' },
  { label: 'Biomecânica da braçada', icon: Waves, prompt: 'Avalie a biomecânica das braçadas: catch, pull, push e recovery.' },
  { label: 'Plano de melhoria', icon: Target, prompt: 'Baseado na análise, crie um plano de melhoria com exercícios específicos.' },
];

const ANALYSIS_STAGES = [
  { key: 'upload', label: 'Enviando vídeo', icon: Eye },
  { key: 'detect', label: 'Detectando corpo', icon: Activity },
  { key: 'skeleton', label: 'Mapeando articulações', icon: Waves },
  { key: 'stroke', label: 'Contando braçadas', icon: Camera },
  { key: 'biomech', label: 'Análise biomecânica', icon: Gauge },
  { key: 'metrics', label: 'Calculando métricas', icon: BarChart3 },
  { key: 'insights', label: 'Gerando insights IA', icon: Brain },
];

// Stroke style display names (pt-BR)
const STROKE_LABELS = {
  'Crawl': { emoji: '🏊', color: '#6366f1' },
  'Freestyle': { emoji: '🏊', color: '#6366f1' },
  'Costas': { emoji: '🔄', color: '#60a5fa' },
  'Backstroke': { emoji: '🔄', color: '#60a5fa' },
  'Peito': { emoji: '🐸', color: '#34d399' },
  'Breaststroke': { emoji: '🐸', color: '#34d399' },
  'Borboleta': { emoji: '🦋', color: '#f59e0b' },
  'Butterfly': { emoji: '🦋', color: '#f59e0b' },
};

// Camera-aware metrics availability
const CAMERA_METRICS_MAP = {
  lateral: { label: 'Lateral', icon: '📷', best: ['stroke_rate', 'cycle_time', 'body_roll', 'elbow_angle', 'kick_amplitude'] },
  frontal: { label: 'Frontal', icon: '📹', best: ['symmetry', 'hand_entry', 'head_alignment', 'shoulder_width'] },
  underwater_lateral: { label: 'Sub. Lateral', icon: '🤿', best: ['pullout_length', 'catch_angle', 'streamline', 'kick_depth', 'body_position'] },
  underwater_bottom: { label: 'Sub. Inferior', icon: '⬇️', best: ['symmetry', 'kick_pattern', 'streamline_width', 'hand_path'] },
  aerial: { label: 'Aérea', icon: '🚁', best: ['lane_tracking', 'pace_consistency', 'turn_time'] },
};

// Metric cards are built dynamically from real pipeline data — no mock constants

// Angle data comes from real pipeline biomechanics.angles — no mock constants
const ANGLE_LABELS = {
  elbow_left: 'Cotovelo Esquerdo', elbow_right: 'Cotovelo Direito',
  shoulder_left: 'Ombro Esquerdo', shoulder_right: 'Ombro Direito',
  knee_left: 'Joelho Esquerdo', knee_right: 'Joelho Direito',
  body_angle: 'Ângulo Corporal', body_roll: 'Body Roll',
  trunk_rotation: 'Rotação do Tronco', left_hip: 'Quadril Esquerdo',
  right_hip: 'Quadril Direito', kick_spread: 'Abertura da Pernada',
  head_position: 'Posição da Cabeça',
};

// Split data comes from real pipeline biomechanics.split_analysis — no mock constants

// Skeleton edge colors (left = blue, right = orange, center = white)
const SKELETON_COLORS = {
  head: '#a78bfa',     // purple
  left: '#60a5fa',     // blue
  right: '#fb923c',    // orange
  center: '#e5e7eb',   // gray
};

const getEdgeColor = (i, j) => {
  if (i <= 4 || j <= 4) return SKELETON_COLORS.head;
  const names = ["nose","left_eye","right_eye","left_ear","right_ear","left_shoulder","right_shoulder","left_elbow","right_elbow","left_wrist","right_wrist","left_hip","right_hip","left_knee","right_knee","left_ankle","right_ankle"];
  const n = names[i] || '';
  if (n.startsWith('left')) return SKELETON_COLORS.left;
  if (n.startsWith('right')) return SKELETON_COLORS.right;
  return SKELETON_COLORS.center;
};

const EVENT_COLORS = {
  cycle_start: '#6366f1',
  cycle_end: '#6366f1',
  hand_entry_left: '#60a5fa',
  hand_entry_right: '#fb923c',
  catch_left: '#60a5fa',
  catch_right: '#fb923c',
  breathing: '#34d399',
};

// ═══════════════════════════════════════════════════════════════════════════════
// Sub-components
// ═══════════════════════════════════════════════════════════════════════════════

const MetricCard = ({ data }) => {
  const Icon = data.icon;
  return (
    <div className="bg-white rounded-xl border p-3 hover:shadow-md transition-all" style={{ borderColor: VUKZ.border }}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={14} style={{ color: VUKZ.textLight }} />
        <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: VUKZ.textLight }}>{data.label}</span>
      </div>
      <div className="flex items-baseline gap-1">
        <span className="text-xl font-black" style={{ color: VUKZ.text }}>{data.value}</span>
        <span className="text-xs font-semibold" style={{ color: VUKZ.textLight }}>{data.unit}</span>
      </div>
    </div>
  );
};

const AngleRow = ({ angle }) => {
  const statusColor = angle.status === 'ok' ? VUKZ.success : angle.status === 'warning' ? VUKZ.warning : VUKZ.error;
  const StatusIcon = angle.status === 'ok' ? CheckCircle2 : AlertTriangle;
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg border hover:shadow-sm transition-all" style={{ borderColor: VUKZ.border }}>
      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${statusColor}10` }}>
        <StatusIcon size={14} style={{ color: statusColor }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-0.5">
          <span className="text-xs font-bold" style={{ color: VUKZ.text }}>{angle.joint}</span>
          <div className="flex items-center gap-2">
            <span className="text-sm font-black" style={{ color: statusColor }}>{angle.value}°</span>
            <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold" style={{ backgroundColor: VUKZ.primaryLight, color: VUKZ.textLight }}>
              ideal: {angle.ideal}
            </span>
          </div>
        </div>
        <p className="text-[10px] leading-relaxed" style={{ color: VUKZ.textLight }}>{angle.detail}</p>
      </div>
    </div>
  );
};

// ── Skeleton Overlay (canvas drawn on top of video) ───────────────────────────
const SkeletonOverlay = ({ width, height, frame, edges, visible }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!visible || !frame || !canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, width, height);

    const kps = frame.keypoints;
    if (!kps || kps.length < 17) return;

    // Draw edges
    (edges || []).forEach(([i, j]) => {
      const a = kps[i], b = kps[j];
      if (!a || !b || a.confidence < 0.5 || b.confidence < 0.5) return;
      ctx.beginPath();
      ctx.moveTo(a.x * width, a.y * height);
      ctx.lineTo(b.x * width, b.y * height);
      ctx.strokeStyle = getEdgeColor(i, j);
      ctx.lineWidth = 2.5;
      ctx.globalAlpha = Math.min(a.confidence, b.confidence);
      ctx.stroke();
      ctx.globalAlpha = 1;
    });

    // Draw keypoints
    kps.forEach((kp) => {
      if (kp.confidence < 0.5) return;
      ctx.beginPath();
      ctx.arc(kp.x * width, kp.y * height, 4, 0, 2 * Math.PI);
      ctx.fillStyle = '#ffffff';
      ctx.globalAlpha = kp.confidence;
      ctx.fill();
      ctx.strokeStyle = '#111111';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.globalAlpha = 1;
    });
  }, [frame, width, height, edges, visible]);

  if (!visible) return null;
  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className="absolute inset-0 pointer-events-none"
      style={{ zIndex: 10 }}
    />
  );
};

// ── Timeline Strip (event markers on a horizontal bar) ────────────────────────
const TimelineStrip = ({ events, duration, currentTime, onSeek }) => {
  if (!events || events.length === 0 || !duration) return null;

  const visibleEvents = events.filter(e => e.event === 'cycle_start' || e.event === 'breathing' || e.event === 'hand_entry_left' || e.event === 'hand_entry_right');

  return (
    <div className="bg-white rounded-xl border p-3 mb-3" style={{ borderColor: VUKZ.border }}>
      <div className="flex items-center gap-2 mb-2">
        <Flame size={12} style={{ color: VUKZ.textLight }} />
        <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: VUKZ.textLight }}>
          Timeline de Eventos ({events.length} detectados)
        </span>
      </div>
      {/* Bar */}
      <div className="relative h-8 rounded-lg cursor-pointer" style={{ backgroundColor: VUKZ.primaryLight }}
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const pct = (e.clientX - rect.left) / rect.width;
          onSeek?.(pct * duration);
        }}
      >
        {/* Current time indicator */}
        <div className="absolute top-0 bottom-0 w-0.5 bg-black z-10" style={{ left: `${(currentTime / duration) * 100}%` }} />

        {/* Event markers */}
        {visibleEvents.map((ev, idx) => {
          const leftPct = (ev.t / duration) * 100;
          const color = EVENT_COLORS[ev.event] || '#6b7280';
          return (
            <div
              key={idx}
              className="absolute top-1 w-1.5 rounded-full"
              style={{
                left: `${leftPct}%`,
                height: ev.event === 'breathing' ? '24px' : '16px',
                backgroundColor: color,
                opacity: 0.8,
              }}
              title={`${ev.t}s — ${ev.label}`}
            />
          );
        })}
      </div>
      {/* Legend */}
      <div className="flex items-center gap-3 mt-2">
        {[
          { color: '#6366f1', label: 'Ciclo' },
          { color: '#60a5fa', label: 'Mão Esq.' },
          { color: '#fb923c', label: 'Mão Dir.' },
          { color: '#34d399', label: 'Respiração' },
        ].map(l => (
          <div key={l.label} className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: l.color }} />
            <span className="text-[9px] font-semibold" style={{ color: VUKZ.textLight }}>{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Alert Card (from backend summary.alerts) ─────────────────────────────────
const AlertCard = ({ alert }) => {
  const isWarn = alert.severity === 'warning';
  const Icon = isWarn ? AlertTriangle : CheckCircle2;
  const color = isWarn ? VUKZ.warning : VUKZ.success;
  return (
    <div className="flex items-start gap-2.5 p-3 rounded-lg border" style={{ borderColor: `${color}30`, backgroundColor: `${color}08` }}>
      <Icon size={14} style={{ color }} className="mt-0.5 flex-shrink-0" />
      <div>
        <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color }}>{alert.metric?.replace(/_/g, ' ')}</span>
        <p className="text-xs leading-relaxed mt-0.5" style={{ color: VUKZ.text }}>{alert.message}</p>
      </div>
    </div>
  );
};

const ChatBubble = ({ msg, onFeedback }) => {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      <div
        className={`max-w-[88%] rounded-2xl px-4 py-3 ${isUser ? 'rounded-br-md' : 'rounded-bl-md'}`}
        style={{
          backgroundColor: isUser ? VUKZ.primary : 'white',
          color: isUser ? 'white' : VUKZ.text,
          border: isUser ? 'none' : `1px solid ${VUKZ.border}`,
        }}
      >
        {msg.confidence && !isUser && (
          <div className="flex items-center gap-1.5 mb-2">
            <Sparkles size={10} style={{ color: VUKZ.textLight }} />
            <span className="text-[9px] font-bold uppercase tracking-wide" style={{ color: VUKZ.textLight }}>
              Confiança: {msg.confidence}
            </span>
          </div>
        )}
        <p className="text-[13px] whitespace-pre-line leading-relaxed">{msg.text}</p>
        {!isUser && msg.showFeedback && (
          <div className="flex items-center gap-2 mt-2 pt-2" style={{ borderTop: `1px solid ${VUKZ.border}` }}>
            <button onClick={() => onFeedback(true)} className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-md hover:bg-gray-50 transition-all" style={{ color: VUKZ.textLight, border: `1px solid ${VUKZ.border}` }}>
              <ThumbsUp size={10} /> Útil
            </button>
            <button onClick={() => onFeedback(false)} className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-md hover:bg-red-50 transition-all" style={{ color: VUKZ.error, border: '1px solid #fecaca' }}>
              <ThumbsDown size={10} /> Incorreto
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Stroke Timing Tab — Full temporal report + official race overlay
// ═══════════════════════════════════════════════════════════════════════════════

const StrokeTimingTab = ({
  strokeChartUrl, strokeTiming,
  startToFirstStrokeSeconds, largestGapSeconds, raceDurationSeconds,
  reactionTimeOfficial, totalTimeOfficial, splitsOfficial, officialResultId,
  sessionId,
}) => {
  const summary = strokeTiming?.summary;
  const strokes = strokeTiming?.strokes || [];
  const [showTable, setShowTable] = React.useState(false);

  // Format seconds as mm:ss.cc or ss.ccs
  const fmtTime = (s) => {
    if (s == null) return '—';
    const min = Math.floor(s / 60);
    const sec = s % 60;
    return min > 0 ? `${min}:${sec.toFixed(2).padStart(5, '0')}` : `${sec.toFixed(2)}s`;
  };

  const hasOfficialData = reactionTimeOfficial != null || totalTimeOfficial != null || (splitsOfficial && splitsOfficial.length > 0);

  if (!strokeChartUrl && !strokeTiming && !hasOfficialData) {
    return (
      <div className="p-6 text-center" style={{ color: VUKZ.textLight }}>
        <Timer size={32} className="mx-auto mb-2 opacity-40" />
        <p className="text-sm font-medium">Dados de timing não disponíveis</p>
        <p className="text-xs mt-1">Processe um novo vídeo para gerar o relatório temporal.</p>
      </div>
    );
  }

  // Classify intervals for color coding
  const getIntervalColor = (interval) => {
    if (!interval || !summary?.medianIntervalS) return VUKZ.text;
    const ratio = interval / summary.medianIntervalS;
    if (ratio > 2.0) return '#dc2626'; // red — missed
    if (ratio > 1.5) return '#f59e0b'; // amber — suspect
    if (ratio < 0.5) return '#8b5cf6'; // purple — overcount
    return VUKZ.text;
  };

  return (
    <div className="p-4 space-y-4">

      {/* ── Section 1: Official Race Reference ────────────────────────── */}
      {hasOfficialData && (
        <div className="rounded-xl border-2 overflow-hidden" style={{ borderColor: '#0ea5e9' }}>
          <div className="px-3 py-2 flex items-center gap-2" style={{ backgroundColor: '#f0f9ff' }}>
            <Activity size={14} style={{ color: '#0ea5e9' }} />
            <span className="text-xs font-black uppercase tracking-wide" style={{ color: '#0369a1' }}>
              Referência Oficial da Prova
            </span>
            {officialResultId && (
              <span className="ml-auto text-[10px] font-mono" style={{ color: '#64748b' }}>
                result #{officialResultId}
              </span>
            )}
          </div>
          <div className="p-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {reactionTimeOfficial != null && (
                <div className="bg-sky-50 rounded-lg p-2.5 text-center">
                  <div className="text-lg font-black" style={{ color: '#0369a1' }}>{fmtTime(reactionTimeOfficial)}</div>
                  <div className="text-[10px] font-bold uppercase tracking-wide text-sky-600">Reação</div>
                </div>
              )}
              {totalTimeOfficial != null && (
                <div className="bg-sky-50 rounded-lg p-2.5 text-center">
                  <div className="text-lg font-black" style={{ color: '#0369a1' }}>{fmtTime(totalTimeOfficial)}</div>
                  <div className="text-[10px] font-bold uppercase tracking-wide text-sky-600">Tempo Total</div>
                </div>
              )}
              {splitsOfficial && splitsOfficial.length > 0 && splitsOfficial.map((sp, i) => (
                <div key={i} className="bg-sky-50 rounded-lg p-2.5 text-center">
                  <div className="text-lg font-black" style={{ color: '#0369a1' }}>{fmtTime(sp.timeS)}</div>
                  <div className="text-[10px] font-bold uppercase tracking-wide text-sky-600">Split {sp.distance}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Section 2: Timing Report Summary ─────────────────────────── */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="bg-gray-50 rounded-xl p-3 text-center">
            <div className="text-lg font-black" style={{ color: VUKZ.text }}>{summary.totalStrokes}</div>
            <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: VUKZ.textLight }}>Braçadas</div>
          </div>
          <div className="bg-gray-50 rounded-xl p-3 text-center">
            <div className="text-lg font-black" style={{ color: VUKZ.text }}>{fmtTime(summary.avgIntervalS)}</div>
            <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: VUKZ.textLight }}>Intervalo Médio</div>
          </div>
          {startToFirstStrokeSeconds != null && (
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <div className="text-lg font-black" style={{ color: VUKZ.text }}>{fmtTime(startToFirstStrokeSeconds)}</div>
              <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: VUKZ.textLight }}>Saída → 1ª Braçada</div>
            </div>
          )}
          {largestGapSeconds != null && (
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <div className="text-lg font-black" style={{ color: largestGapSeconds > (summary.medianIntervalS * 2) ? '#dc2626' : VUKZ.text }}>
                {fmtTime(largestGapSeconds)}
              </div>
              <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: VUKZ.textLight }}>Maior Gap</div>
            </div>
          )}
          {raceDurationSeconds != null && (
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <div className="text-lg font-black" style={{ color: VUKZ.text }}>{fmtTime(raceDurationSeconds)}</div>
              <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: VUKZ.textLight }}>Duração Prova</div>
            </div>
          )}
          <div className="bg-gray-50 rounded-xl p-3 text-center">
            <div className="text-lg font-black" style={{ color: VUKZ.text }}>{fmtTime(summary.medianIntervalS)}</div>
            <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: VUKZ.textLight }}>Mediana</div>
          </div>
        </div>
      )}

      {/* ── Section 3: Gap Alerts ─────────────────────────────────────── */}
      {summary && (summary.suspectGaps > 0 || summary.missedGaps > 0 || summary.overcountGaps > 0 || summary.withinExpectedWindow != null) && (
        <div className="flex flex-wrap gap-2">
          {summary.missedGaps > 0 && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-red-50 text-red-700 border border-red-200">
              <AlertTriangle size={12} /> {summary.missedGaps} gap{summary.missedGaps > 1 ? 's' : ''} perdido{summary.missedGaps > 1 ? 's' : ''}
            </span>
          )}
          {summary.suspectGaps > 0 && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
              <AlertTriangle size={12} /> {summary.suspectGaps} suspeito{summary.suspectGaps > 1 ? 's' : ''}
            </span>
          )}
          {summary.overcountGaps > 0 && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-purple-50 text-purple-700 border border-purple-200">
              <AlertTriangle size={12} /> {summary.overcountGaps} overcount{summary.overcountGaps > 1 ? 's' : ''}
            </span>
          )}
          {summary.withinExpectedWindow != null && (
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border ${
              summary.withinExpectedWindow
                ? 'bg-green-50 text-green-700 border-green-200'
                : 'bg-red-50 text-red-700 border-red-200'
            }`}>
              {summary.withinExpectedWindow ? <CheckCircle2 size={12} /> : <AlertTriangle size={12} />}
              {summary.withinExpectedWindow ? 'Dentro da faixa esperada' : 'Fora da faixa esperada'}
            </span>
          )}
        </div>
      )}

      {/* ── Section 4: Timing Range Bar ──────────────────────────────── */}
      {summary && summary.minIntervalS && summary.maxIntervalS && (
        <div className="bg-gray-50 rounded-xl p-3">
          <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wide mb-2" style={{ color: VUKZ.textLight }}>
            <span>Variação dos Intervalos</span>
            <span>{fmtTime(summary.minIntervalS)} → {fmtTime(summary.maxIntervalS)}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold" style={{ color: VUKZ.success }}>{fmtTime(summary.minIntervalS)}</span>
            <div className="flex-1 h-3 rounded-full bg-gray-200 relative overflow-hidden">
              {/* Median marker */}
              <div
                className="absolute inset-y-0 rounded-full"
                style={{
                  backgroundColor: VUKZ.primary,
                  left: '0%',
                  width: summary.maxIntervalS > 0 ? `${Math.min(100, (summary.avgIntervalS / summary.maxIntervalS) * 100)}%` : '50%',
                }}
              />
              {/* Largest gap marker */}
              {largestGapSeconds && largestGapSeconds > summary.avgIntervalS && (
                <div
                  className="absolute inset-y-0 w-1 rounded-full"
                  style={{
                    backgroundColor: '#dc2626',
                    left: `${Math.min(100, (largestGapSeconds / summary.maxIntervalS) * 100)}%`,
                  }}
                />
              )}
            </div>
            <span className="text-xs font-bold" style={{ color: VUKZ.error }}>{fmtTime(summary.maxIntervalS)}</span>
          </div>
        </div>
      )}

      {/* ── Section 5: Chart Image ───────────────────────────────────── */}
      {strokeChartUrl && (
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: VUKZ.border }}>
          <div className="px-3 py-2 border-b flex items-center gap-2" style={{ borderColor: VUKZ.border, backgroundColor: '#f9fafb' }}>
            <BarChart3 size={14} style={{ color: VUKZ.textLight }} />
            <span className="text-xs font-bold" style={{ color: VUKZ.text }}>Gráfico de Timing das Braçadas</span>
            {hasOfficialData && (
              <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full bg-sky-50 text-sky-700">
                + tempos oficiais
              </span>
            )}
          </div>
          <img
            src={strokeChartUrl}
            alt="Stroke Timing Chart"
            className="w-full"
            style={{ maxHeight: '600px', objectFit: 'contain', backgroundColor: '#fff' }}
            loading="lazy"
          />
        </div>
      )}

      {/* ── Section 6: Stroke-by-stroke Table ────────────────────────── */}
      {strokes.length > 0 && (
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: VUKZ.border }}>
          <button
            onClick={() => setShowTable(!showTable)}
            className="w-full px-3 py-2 text-xs font-bold flex items-center gap-2 select-none hover:bg-gray-50 transition-colors"
            style={{ color: VUKZ.text }}
          >
            <ChevronRight size={14} className={`transition-transform ${showTable ? 'rotate-90' : ''}`} />
            Braçada a braçada ({strokes.length} registros)
          </button>
          {showTable && (
            <div className="max-h-80 overflow-y-auto border-t" style={{ borderColor: VUKZ.border }}>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b sticky top-0" style={{ borderColor: VUKZ.border, backgroundColor: '#f9fafb' }}>
                    <th className="px-3 py-2 text-left font-bold" style={{ color: VUKZ.textLight }}>#</th>
                    <th className="px-3 py-2 text-right font-bold" style={{ color: VUKZ.textLight }}>Tempo Absoluto</th>
                    <th className="px-3 py-2 text-right font-bold" style={{ color: VUKZ.textLight }}>Intervalo</th>
                    <th className="px-3 py-2 text-center font-bold" style={{ color: VUKZ.textLight }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {strokes.map((s) => {
                    const ivColor = getIntervalColor(s.intervalS);
                    const isGap = s.intervalS && summary?.medianIntervalS && (s.intervalS / summary.medianIntervalS) > 1.5;
                    return (
                      <tr
                        key={s.number}
                        className={`border-b last:border-0 hover:bg-gray-50 ${isGap ? 'bg-red-50/50' : ''}`}
                        style={{ borderColor: VUKZ.border }}
                      >
                        <td className="px-3 py-1.5 font-bold" style={{ color: VUKZ.text }}>{s.number}</td>
                        <td className="px-3 py-1.5 text-right font-mono" style={{ color: VUKZ.text }}>{fmtTime(s.videoTimeS)}</td>
                        <td className="px-3 py-1.5 text-right font-mono font-bold" style={{ color: ivColor }}>
                          {s.intervalS != null ? fmtTime(s.intervalS) : '—'}
                        </td>
                        <td className="px-3 py-1.5 text-center">
                          {s.intervalS && summary?.medianIntervalS ? (
                            s.intervalS / summary.medianIntervalS > 2.0 ? (
                              <span className="text-[10px] font-bold text-red-600">PERDIDA</span>
                            ) : s.intervalS / summary.medianIntervalS > 1.5 ? (
                              <span className="text-[10px] font-bold text-amber-600">SUSPEITO</span>
                            ) : s.intervalS / summary.medianIntervalS < 0.5 ? (
                              <span className="text-[10px] font-bold text-purple-600">EXTRA</span>
                            ) : (
                              <span className="text-[10px] text-green-600">OK</span>
                            )
                          ) : (
                            <span className="text-[10px]" style={{ color: VUKZ.textLight }}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Section 7: Evolução Acumulada (progress through race) ────── */}
      {strokes.length > 3 && raceDurationSeconds && (
        <div className="bg-gray-50 rounded-xl p-3">
          <div className="text-[10px] font-bold uppercase tracking-wide mb-2" style={{ color: VUKZ.textLight }}>
            Evolução Acumulada da Prova
          </div>
          <div className="flex items-end gap-[2px] h-12">
            {strokes.map((s, i) => {
              const progress = raceDurationSeconds > 0
                ? ((s.videoTimeS - strokes[0].videoTimeS) / raceDurationSeconds) * 100
                : ((i + 1) / strokes.length) * 100;
              const ivColor = getIntervalColor(s.intervalS);
              return (
                <div
                  key={s.number}
                  className="flex-1 rounded-t"
                  style={{
                    height: `${Math.max(8, progress)}%`,
                    backgroundColor: ivColor === VUKZ.text ? VUKZ.primary : ivColor,
                    opacity: 0.7,
                    minWidth: '2px',
                  }}
                  title={`#${s.number}: ${fmtTime(s.videoTimeS)} (${s.intervalS ? fmtTime(s.intervalS) : '—'})`}
                />
              );
            })}
          </div>
          <div className="flex justify-between text-[9px] mt-1" style={{ color: VUKZ.textLight }}>
            <span>{fmtTime(summary?.firstStrokeVideoS)}</span>
            <span>{fmtTime(summary?.lastStrokeVideoS)}</span>
          </div>
        </div>
      )}

      {/* ── Section 8: Expected Window + Metadata ────────────────────── */}
      <div className="flex flex-wrap items-center justify-center gap-3 text-[10px]" style={{ color: VUKZ.textLight }}>
        {summary?.expectedStartS && summary?.expectedEndS && (
          <span>Faixa esperada: {fmtTime(summary.expectedStartS)} → {fmtTime(summary.expectedEndS)}</span>
        )}
        {totalTimeOfficial && raceDurationSeconds && (
          <span className="font-bold" style={{ color: '#0369a1' }}>
            Δ oficial: {fmtTime(Math.abs(totalTimeOfficial - raceDurationSeconds))}
          </span>
        )}
      </div>

      {/* ── Link to full Race Dashboard ────────────────────── */}
      {sessionId && (strokeTiming || reactionTimeOfficial != null) && (
        <div className="pt-3 border-t text-center" style={{ borderColor: VUKZ.border }}>
          <Link
            to={`/swim-vision/race/${sessionId}`}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all hover:opacity-90"
            style={{ backgroundColor: '#111111', color: '#ffffff' }}
          >
            <BarChart3 size={15} />
            Dashboard da Prova
          </Link>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Race Dashboard Tab — Inline dashboard of the linked race/prova
// ═══════════════════════════════════════════════════════════════════════════════

const DASH = {
  accent: '#111111', cyan: '#0891b2', cyanBg: '#ecfeff', info: '#2563eb', infoBg: '#eff6ff',
  success: '#16a34a', successBg: '#f0fdf4', warning: '#f59e0b', warningBg: '#fffbeb',
  error: '#dc2626', errorBg: '#fef2f2', purple: '#7c3aed', purpleBg: '#f5f3ff',
  reactionLine: '#0284c7', splitLine: '#0ea5e9', totalLine: '#1d4ed8',
};

const STYLE_LABELS_DASH = {
  freestyle: 'Livre', backstroke: 'Costas', butterfly: 'Borboleta',
  breaststroke: 'Peito', medley: 'Medley',
};

const RaceDashboardTab = ({ apiData, sessionId }) => {
  const [raceCtx, setRaceCtx] = React.useState(null);
  const [loadingCtx, setLoadingCtx] = React.useState(true);

  React.useEffect(() => {
    if (!sessionId) { setLoadingCtx(false); return; }
    (async () => {
      try {
        const resp = await getRaceContext(sessionId);
        const d = resp.data?.data || resp.data;
        setRaceCtx(d?.linked ? d.context : null);
      } catch { /* no race context */ }
      setLoadingCtx(false);
    })();
  }, [sessionId]);

  const strokeTiming = apiData?.strokeTiming;
  const strokes = strokeTiming?.strokes || [];
  const summary = strokeTiming?.summary || {};
  const reaction = apiData?.reactionTimeOfficial;
  const totalOfficial = apiData?.totalTimeOfficial;
  const splitsOfficial = apiData?.splitsOfficial || [];
  const startToFirst = apiData?.startToFirstStrokeS;
  const largestGap = apiData?.largestGapS;
  const raceDuration = apiData?.raceDurationSeconds;
  const style = apiData?.swimmingStyle;
  // Canonical stroke count: single source from normalizeSession
  const strokeCount = apiData?.canonicalStrokeCount || 0;
  const hasStrokeData = strokes.length > 0;
  const hasOfficialData = reaction != null || totalOfficial != null || splitsOfficial.length > 0;
  const lane = raceCtx?.lanes?.[0];

  const fmtT = (s) => {
    if (s == null || isNaN(s)) return '—';
    const min = Math.floor(s / 60);
    const sec = s % 60;
    return min > 0 ? `${min}:${sec.toFixed(2).padStart(5, '0')}` : `${sec.toFixed(2)}s`;
  };

  // ── Race origin ──
  const raceOrigin = React.useMemo(() => {
    if (summary.expectedStartS > 0) return summary.expectedStartS;
    if (strokes.length > 0) return Math.max(0, strokes[0].videoTimeS - 2);
    return 0;
  }, [summary, strokes]);

  // ── Cumulative chart data ──
  const cumulativeData = React.useMemo(() => {
    if (!strokes.length) return [];
    const cum = [{ timeS: 0, strokes: 0 }];
    strokes.forEach(s => cum.push({ timeS: +(s.videoTimeS - raceOrigin).toFixed(3), strokes: s.number }));
    return cum;
  }, [strokes, raceOrigin]);

  // ── Interval chart data ──
  // v3.9: Use style-specific interval limits when available (from backend STYLE_INTERVAL_CFG)
  const styleLimits = apiData?.styleIntervalLimits;
  const intervalData = React.useMemo(() => {
    if (!strokes.length) return [];
    const median = summary.medianIntervalS || summary.avgIntervalS || 1;
    const surfaceMax = styleLimits?.surfaceMaxDt || (median * 2.5);
    const surfaceMin = styleLimits?.surfaceMinDt || (median * 0.4);
    const transitionMax = styleLimits?.transitionMaxDt || (median * 4.0);
    return strokes.map((s, i) => {
      const t = +(s.videoTimeS - raceOrigin).toFixed(3);
      let iv, status;
      if (i === 0) {
        iv = startToFirst != null ? startToFirst : (s.videoTimeS - raceOrigin);
        status = 'first';
      } else {
        iv = s.intervalS;
        const isTransition = s.phase === 'start_breakout' || s.phase === 'turn_breakout';
        const maxAllowed = isTransition ? transitionMax : surfaceMax;
        status = iv == null ? 'ok'
          : iv > maxAllowed ? 'missed'
          : iv > surfaceMax * 0.8 ? 'suspect'
          : iv < surfaceMin ? 'overcount'
          : 'ok';
      }
      return {
        timeS: t, interval: iv != null ? +iv.toFixed(3) : null, status,
        lap: s.lap || 1, phase: s.phase || 'surface', recovered: s.recovered || false,
      };
    }).filter(d => d.interval != null);
  }, [strokes, raceOrigin, startToFirst, summary, styleLimits]);

  const medianInterval = summary.medianIntervalS || summary.avgIntervalS || 1;

  // ── Official reference lines ──
  const officialLines = React.useMemo(() => {
    const lines = [];
    if (reaction != null) lines.push({ x: reaction, label: `Reação ${fmtT(reaction)}`, color: DASH.reactionLine, dash: '6 3' });
    splitsOfficial.forEach(sp => {
      if (sp.timeS != null) lines.push({ x: sp.timeS, label: sp.distance, color: DASH.splitLine, dash: '4 4' });
    });
    if (totalOfficial != null) lines.push({ x: totalOfficial, label: `Final ${fmtT(totalOfficial)}`, color: DASH.totalLine, dash: '0' });
    return lines;
  }, [reaction, splitsOfficial, totalOfficial]);

  if (!hasStrokeData && !hasOfficialData) {
    return (
      <div className="p-8 text-center" style={{ color: VUKZ.textLight }}>
        <LayoutDashboard size={36} className="mx-auto mb-3 opacity-40" />
        <p className="text-sm font-bold" style={{ color: VUKZ.text }}>Dashboard da Prova</p>
        <p className="text-xs mt-1">Processe um vídeo com dados de braçada para visualizar o dashboard.</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">

      {/* ── Header: Race Context ──────────────────────────────────── */}
      <div className="rounded-xl border-2 overflow-hidden" style={{ borderColor: VUKZ.primary }}>
        <div className="px-3 py-2 flex items-center gap-2" style={{ backgroundColor: VUKZ.primary + '0a' }}>
          <LayoutDashboard size={14} style={{ color: VUKZ.primary }} />
          <span className="text-xs font-black uppercase tracking-wide" style={{ color: VUKZ.primary }}>
            Dashboard da Prova
          </span>
          {raceCtx && (
            <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full"
              style={{ backgroundColor: DASH.successBg, color: DASH.success }}>
              ✓ Vinculada
            </span>
          )}
        </div>

        <div className="p-3 space-y-3">
          {/* Race metadata chips */}
          {loadingCtx ? (
            <div className="flex items-center gap-2 text-xs" style={{ color: VUKZ.textLight }}>
              <RefreshCw size={12} className="animate-spin" /> Carregando contexto...
            </div>
          ) : raceCtx ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              {[
                { icon: Zap, label: 'Competição', value: raceCtx.competition_name },
                { icon: Activity, label: 'Prova', value: raceCtx.event_name },
                { icon: Target, label: 'Fase', value: raceCtx.phase },
                { icon: Waves, label: 'Estilo', value: STYLE_LABELS_DASH[style] || style },
                ...(lane ? [
                  { icon: Ruler, label: 'Raia', value: lane.lane_number },
                  { icon: Droplets, label: 'Atleta', value: lane.athlete_name },
                ] : []),
              ].map((c, i) => (
                <div key={i} className="rounded-lg p-2" style={{ backgroundColor: '#f9fafb' }}>
                  <div className="flex items-center gap-1 mb-0.5">
                    <c.icon size={9} style={{ color: VUKZ.textLight }} />
                    <span className="text-[8px] font-bold uppercase tracking-wider" style={{ color: VUKZ.textLight }}>{c.label}</span>
                  </div>
                  <p className="text-[11px] font-bold truncate" style={{ color: VUKZ.text }}>{c.value ?? '—'}</p>
                </div>
              ))}
            </div>
          ) : (apiData?.athlete_name || apiData?.competition_name) ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              {[
                ...(apiData.competition_name ? [{ icon: Zap, label: 'Competição', value: apiData.competition_name }] : []),
                ...(apiData.fase ? [{ icon: Target, label: 'Fase', value: apiData.fase }] : []),
                { icon: Waves, label: 'Estilo', value: STYLE_LABELS_DASH[style] || style },
                ...(apiData.lane_number ? [{ icon: Ruler, label: 'Raia', value: apiData.lane_number }] : []),
                ...(apiData.athlete_name ? [{ icon: Droplets, label: 'Atleta', value: apiData.athlete_name }] : []),
              ].map((c, i) => (
                <div key={i} className="rounded-lg p-2" style={{ backgroundColor: '#f9fafb' }}>
                  <div className="flex items-center gap-1 mb-0.5">
                    <c.icon size={9} style={{ color: VUKZ.textLight }} />
                    <span className="text-[8px] font-bold uppercase tracking-wider" style={{ color: VUKZ.textLight }}>{c.label}</span>
                  </div>
                  <p className="text-[11px] font-bold truncate" style={{ color: VUKZ.text }}>{c.value ?? '—'}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-[11px] p-2 rounded-lg text-center" style={{ backgroundColor: DASH.warningBg, color: DASH.warning }}>
              <AlertTriangle size={12} className="inline mr-1" />
              Prova não vinculada a competição. Vincule para dados completos.
            </div>
          )}

          {/* Official data row */}
          {hasOfficialData && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-2.5 rounded-lg border" style={{ borderColor: '#bae6fd', backgroundColor: '#f0f9ff' }}>
              <div>
                <span className="text-[8px] font-bold uppercase tracking-wider" style={{ color: DASH.cyan }}>Reação</span>
                <p className="text-sm font-black" style={{ color: VUKZ.text }}>{fmtT(reaction)}</p>
              </div>
              <div>
                <span className="text-[8px] font-bold uppercase tracking-wider" style={{ color: DASH.cyan }}>Tempo Final</span>
                <p className="text-sm font-black" style={{ color: VUKZ.text }}>{fmtT(totalOfficial)}</p>
              </div>
              <div className="col-span-2">
                <span className="text-[8px] font-bold uppercase tracking-wider" style={{ color: DASH.cyan }}>Splits</span>
                <div className="flex flex-wrap gap-1.5 mt-0.5">
                  {splitsOfficial.map((sp, i) => (
                    <span key={i} className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: '#e0f2fe', color: DASH.info }}>
                      {sp.distance}: {fmtT(sp.timeS)}
                    </span>
                  ))}
                  {splitsOfficial.length === 0 && <span className="text-[10px]" style={{ color: VUKZ.textLight }}>—</span>}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Summary Cards ─────────────────────────────────────────── */}
      {hasStrokeData && (
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          {[
            { icon: Zap, label: 'Saída→1ª', value: fmtT(startToFirst), color: DASH.cyan, hl: true },
            { icon: Timer, label: 'Média', value: fmtT(summary.avgIntervalS), color: DASH.accent },
            { icon: Clock, label: 'Mediana', value: fmtT(summary.medianIntervalS), color: DASH.purple },
            { icon: AlertTriangle, label: 'Maior gap', value: fmtT(largestGap || summary.maxIntervalS), color: DASH.error, hl: (largestGap || 0) > medianInterval * 2.5 },
            { icon: CheckCircle2, label: 'Menor gap', value: fmtT(summary.minIntervalS), color: DASH.success },
            { icon: Activity, label: 'Total IA', value: `${strokeCount}`, color: DASH.accent },
            { icon: TrendingUp, label: 'Duração', value: fmtT(raceDuration), color: DASH.info },
          ].map((c, i) => (
            <div key={i} className={`rounded-lg border p-2 ${c.hl ? 'ring-1 ring-offset-1' : ''}`}
              style={{ borderColor: c.hl ? c.color : VUKZ.border, ringColor: c.hl ? c.color : undefined }}>
              <div className="flex items-center gap-1 mb-0.5">
                <c.icon size={10} style={{ color: c.color || VUKZ.textLight }} />
                <span className="text-[8px] font-bold uppercase tracking-wider" style={{ color: VUKZ.textLight }}>{c.label}</span>
              </div>
              <span className="text-sm font-black" style={{ color: VUKZ.text }}>{c.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Gap Analysis Alert (v3.9) ─────────────────────────────── */}
      {hasStrokeData && apiData?.gapAnalysis && apiData.gapAnalysis.critical_gaps > 0 && (
        <div className="rounded-xl border-2 p-3" style={{ borderColor: DASH.warning, backgroundColor: DASH.warningBg }}>
          <div className="flex items-center gap-2 mb-1">
            <AlertTriangle size={14} style={{ color: DASH.warning }} />
            <span className="text-[11px] font-black uppercase tracking-wide" style={{ color: DASH.warning }}>
              Gaps Críticos Detectados
            </span>
            <span className="text-[9px] px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
              {apiData.gapAnalysis.critical_gaps} gap{apiData.gapAnalysis.critical_gaps > 1 ? 's' : ''}
            </span>
            {apiData.gapAnalysis.recovered_strokes > 0 && (
              <span className="text-[9px] px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: DASH.successBg, color: DASH.success }}>
                +{apiData.gapAnalysis.recovered_strokes} recuperada{apiData.gapAnalysis.recovered_strokes > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {(apiData.gapAnalysis.gaps || []).slice(0, 5).map((g, i) => (
              <div key={i} className="text-[10px] px-2 py-1 rounded-lg bg-white border"
                style={{ borderColor: g.severity === 'critical' ? '#fca5a5' : '#fde68a' }}>
                <span className="font-bold" style={{ color: g.severity === 'critical' ? DASH.error : DASH.warning }}>
                  {g.dt.toFixed(1)}s
                </span>
                <span className="ml-1" style={{ color: VUKZ.textLight }}>
                  ({fmtT(g.gap_start)}→{fmtT(g.gap_end)})
                </span>
                {g.estimated_missing > 0 && (
                  <span className="ml-1 font-bold" style={{ color: DASH.error }}>
                    ~{g.estimated_missing} perdida{g.estimated_missing > 1 ? 's' : ''}
                  </span>
                )}
                {g.reason && (
                  <span className="ml-1" style={{ color: VUKZ.textLight }}>
                    — {g.reason === 'low_keypoint_visibility' ? 'baixa visibilidade' : 'braçadas perdidas'}
                  </span>
                )}
              </div>
            ))}
          </div>
          {styleLimits && (
            <div className="mt-2 flex gap-3 text-[9px]" style={{ color: VUKZ.textLight }}>
              <span>Limite superfície: {styleLimits.surfaceMaxDt}s</span>
              <span>Limite transição: {styleLimits.transitionMaxDt}s</span>
            </div>
          )}
        </div>
      )}

      {/* ── Chart 1: Cumulative Stroke Count ──────────────────────── */}
      {hasStrokeData && cumulativeData.length > 0 && (
        <div className="rounded-xl border p-3" style={{ borderColor: VUKZ.border }}>
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp size={13} style={{ color: VUKZ.primary }} />
            <span className="text-[11px] font-black uppercase tracking-wide" style={{ color: VUKZ.text }}>
              Evolução Acumulada
            </span>
            <span className="text-[9px] px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: '#f3f4f6', color: VUKZ.textLight }}>
              {strokeCount} braçadas
            </span>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={cumulativeData} margin={{ top: 5, right: 15, left: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={VUKZ.border} />
              <XAxis dataKey="timeS" type="number" domain={['dataMin', 'dataMax']}
                tickFormatter={fmtT} tick={{ fontSize: 9, fill: VUKZ.textLight }} />
              <YAxis tick={{ fontSize: 9, fill: VUKZ.textLight }}
                label={{ value: 'Braçadas', angle: -90, position: 'insideLeft', fontSize: 9, fill: VUKZ.textLight }} />
              <Tooltip formatter={(v) => [v, 'Braçadas']}
                labelFormatter={(v) => `Tempo: ${fmtT(v)}`}
                contentStyle={{ fontSize: 11, borderRadius: 8 }} />
              {officialLines.map((ol, i) => (
                <ReferenceLine key={i} x={ol.x} stroke={ol.color} strokeDasharray={ol.dash} strokeWidth={1.5}
                  label={{ value: ol.label, position: 'top', fontSize: 8, fill: ol.color, fontWeight: 700 }} />
              ))}
              <Line type="stepAfter" dataKey="strokes" stroke={VUKZ.primary} strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
          {/* Legend */}
          <div className="flex flex-wrap items-center gap-3 mt-1 text-[9px]" style={{ color: VUKZ.textLight }}>
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 inline-block" style={{ backgroundColor: VUKZ.primary }} /> Braçadas IA</span>
            {reaction != null && <span className="flex items-center gap-1"><span className="w-3 h-0.5 inline-block" style={{ backgroundColor: DASH.reactionLine }} /> Reação</span>}
            {splitsOfficial.length > 0 && <span className="flex items-center gap-1"><span className="w-3 h-0.5 inline-block" style={{ backgroundColor: DASH.splitLine }} /> Splits</span>}
            {totalOfficial != null && <span className="flex items-center gap-1"><span className="w-3 h-0.5 inline-block" style={{ backgroundColor: DASH.totalLine }} /> Final</span>}
          </div>
        </div>
      )}

      {/* ── Chart 2: Interval Between Strokes ─────────────────────── */}
      {hasStrokeData && intervalData.length > 0 && (
        <div className="rounded-xl border p-3" style={{ borderColor: VUKZ.border }}>
          <div className="flex items-center gap-2 mb-3">
            <Timer size={13} style={{ color: DASH.warning }} />
            <span className="text-[11px] font-black uppercase tracking-wide" style={{ color: VUKZ.text }}>
              Intervalo entre Braçadas
            </span>
            <span className="text-[9px] px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: DASH.warningBg, color: DASH.warning }}>
              Mediana: {fmtT(medianInterval)}
            </span>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={intervalData} margin={{ top: 5, right: 15, left: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={VUKZ.border} />
              <XAxis dataKey="timeS" type="number" domain={['dataMin', 'dataMax']}
                tickFormatter={fmtT} tick={{ fontSize: 9, fill: VUKZ.textLight }} />
              <YAxis domain={[0, 'auto']} tick={{ fontSize: 9, fill: VUKZ.textLight }}
                label={{ value: 'Intervalo (s)', angle: -90, position: 'insideLeft', fontSize: 9, fill: VUKZ.textLight }} />
              <Tooltip formatter={(v) => [fmtT(v), 'Intervalo']}
                labelFormatter={(v) => `Tempo: ${fmtT(v)}`}
                contentStyle={{ fontSize: 11, borderRadius: 8 }} />
              {/* Threshold lines — v3.9: use style-specific limits when available */}
              <ReferenceLine y={medianInterval} stroke={VUKZ.primary} strokeDasharray="6 3" strokeWidth={1}
                label={{ value: `Med ${fmtT(medianInterval)}`, position: 'right', fontSize: 8, fill: VUKZ.primary }} />
              {styleLimits ? (
                <>
                  <ReferenceLine y={styleLimits.surfaceMaxDt} stroke={DASH.warning} strokeDasharray="3 3" strokeWidth={1}
                    label={{ value: `Max ${styleLimits.surfaceMaxDt}s`, position: 'right', fontSize: 7, fill: DASH.warning }} />
                  <ReferenceLine y={styleLimits.transitionMaxDt} stroke={DASH.error} strokeDasharray="3 3" strokeWidth={1}
                    label={{ value: `Transição ${styleLimits.transitionMaxDt}s`, position: 'right', fontSize: 7, fill: DASH.error }} />
                  <ReferenceLine y={styleLimits.surfaceMinDt} stroke={DASH.purple} strokeDasharray="3 3" strokeWidth={1}
                    label={{ value: `Min ${styleLimits.surfaceMinDt}s`, position: 'right', fontSize: 7, fill: DASH.purple }} />
                </>
              ) : (
                <>
                  <ReferenceLine y={medianInterval * 1.8} stroke={DASH.warning} strokeDasharray="3 3" strokeWidth={1}
                    label={{ value: 'Suspeito', position: 'right', fontSize: 7, fill: DASH.warning }} />
                  <ReferenceLine y={medianInterval * 2.5} stroke={DASH.error} strokeDasharray="3 3" strokeWidth={1}
                    label={{ value: 'Perdida', position: 'right', fontSize: 7, fill: DASH.error }} />
                </>
              )}
              {/* Official vertical lines */}
              {officialLines.map((ol, i) => (
                <ReferenceLine key={i} x={ol.x} stroke={ol.color} strokeDasharray={ol.dash} strokeWidth={1} />
              ))}
              {/* Interval line with colored dots */}
              <Line type="monotone" dataKey="interval" stroke={DASH.warning} strokeWidth={2}
                dot={({ cx, cy, payload }) => {
                  const clr = payload.status === 'missed' ? DASH.error
                    : payload.status === 'suspect' ? DASH.warning
                    : payload.status === 'overcount' ? DASH.purple
                    : payload.status === 'first' ? DASH.cyan
                    : DASH.success;
                  const r = payload.status === 'first' ? 5 : payload.status === 'ok' ? 2.5 : 4;
                  // v3.9: Recovered strokes get a distinct ring
                  if (payload.recovered) {
                    return <circle cx={cx} cy={cy} r={4} fill="none" stroke={DASH.success} strokeWidth={2} />;
                  }
                  return <circle cx={cx} cy={cy} r={r} fill={clr} stroke="#fff" strokeWidth={1} />;
                }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
          {/* Legend */}
          <div className="flex flex-wrap items-center gap-2 mt-1 text-[9px]" style={{ color: VUKZ.textLight }}>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: DASH.cyan }} /> Saída→1ª</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: DASH.success }} /> OK</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: DASH.warning }} /> Suspeito</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: DASH.error }} /> Perdida</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: DASH.purple }} /> Extra</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full inline-block border-2" style={{ borderColor: DASH.success, backgroundColor: 'transparent' }} /> Recuperada</span>
            {styleLimits && (
              <span className="ml-auto font-bold" style={{ color: VUKZ.textLight }}>
                Limites: {styleLimits.surfaceMinDt}–{styleLimits.surfaceMaxDt}s
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── Link to full-page dashboard ───────────────────────────── */}
      {sessionId && (
        <div className="text-center pt-2">
          <Link
            to={`/swim-vision/result/${sessionId}`}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all hover:opacity-90"
            style={{ backgroundColor: VUKZ.primary, color: '#ffffff' }}
          >
            <Maximize2 size={13} />
            Abrir Resultado Completo
          </Link>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Link Race Modal — Pre-analysis race linking with cascading dropdowns
// ═══════════════════════════════════════════════════════════════════════════════

const LinkRaceModal = ({ open, onClose, onSave }) => {
  const [competitions, setCompetitions] = React.useState([]);
  const [events, setEvents] = React.useState([]);
  const [lanes, setLanes] = React.useState([]);
  const [loadingComps, setLoadingComps] = React.useState(false);
  const [loadingEvents, setLoadingEvents] = React.useState(false);
  const [loadingLanes, setLoadingLanes] = React.useState(false);

  const [selectedComp, setSelectedComp] = React.useState(null);
  const [selectedEvent, setSelectedEvent] = React.useState(null);
  const [selectedPhase, setSelectedPhase] = React.useState('Final');
  const [compSearch, setCompSearch] = React.useState('');
  const [genderFilter, setGenderFilter] = React.useState(null); // null = all, 'M' or 'F'
  const [selectedLaneIds, setSelectedLaneIds] = React.useState(new Set()); // Set of lane numbers selected by user

  // Load competitions on mount
  React.useEffect(() => {
    if (!open) return;
    (async () => {
      setLoadingComps(true);
      try {
        const comps = await getCompetitions();
        setCompetitions(Array.isArray(comps) ? comps : []);
      } catch { setCompetitions([]); }
      setLoadingComps(false);
    })();
  }, [open]);

  // Load events when competition changes
  React.useEffect(() => {
    if (!selectedComp) { setEvents([]); return; }
    (async () => {
      setLoadingEvents(true);
      try {
        const resp = await getCompetitionEventsWithPhases(selectedComp.id);
        setEvents(resp?.events || []);
      } catch { setEvents([]); }
      setLoadingEvents(false);
    })();
  }, [selectedComp]);

  // Load lanes when event + phase selected
  React.useEffect(() => {
    if (!selectedComp || !selectedEvent) { setLanes([]); setSelectedLaneIds(new Set()); return; }
    (async () => {
      setLoadingLanes(true);
      try {
        const resp = await getRaceHeats(selectedComp.id, selectedEvent.event_id, selectedPhase);
        setLanes(resp?.data?.data?.lanes || []);
      } catch { setLanes([]); }
      setSelectedLaneIds(new Set());
      setLoadingLanes(false);
    })();
  }, [selectedComp, selectedEvent, selectedPhase]);

  const availablePhases = selectedEvent?.phases || ['Final'];

  const toggleLane = (laneNum) => {
    setSelectedLaneIds(prev => {
      const next = new Set(prev);
      if (next.has(laneNum)) next.delete(laneNum);
      else next.add(laneNum);
      return next;
    });
  };

  const handleSave = () => {
    const finalLanes = selectedLaneIds.size > 0
      ? lanes.filter(l => selectedLaneIds.has(l.lane))
      : lanes;
    onSave({
      competition_id: selectedComp.id,
      competition_name: selectedComp.name,
      event_id: selectedEvent.event_id,
      event_name: selectedEvent.event_name,
      phase: selectedPhase,
      lanes: finalLanes,
    });
    onClose();
  };

  const fmtTime = (s) => {
    if (s == null) return '—';
    const min = Math.floor(s / 60);
    const sec = s % 60;
    return min > 0 ? `${min}:${sec.toFixed(2).padStart(5, '0')}` : `${sec.toFixed(2)}s`;
  };

  if (!open) return null;

  const filteredComps = competitions.filter(c =>
    !compSearch || c.name?.toLowerCase().includes(compSearch.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col m-4" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: VUKZ.border }}>
          <div className="flex items-center gap-2">
            <Link2 size={18} style={{ color: VUKZ.primary }} />
            <h2 className="text-base font-black" style={{ color: VUKZ.text }}>Vincular Prova</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={18} style={{ color: VUKZ.textLight }} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">

          {/* Step 1: Competition */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider mb-1.5 block" style={{ color: VUKZ.textLight }}>
              1. Competição
            </label>
            {selectedComp ? (
              <div className="flex items-center gap-2">
                <div className="flex-1 px-3 py-2 rounded-lg text-xs font-bold" style={{ backgroundColor: '#f0fdf4', color: '#15803d', border: '1px solid #86efac' }}>
                  <Trophy size={12} className="inline mr-1" />
                  {selectedComp.name} {selectedComp.year && `(${selectedComp.year})`}
                </div>
                <button onClick={() => { setSelectedComp(null); setSelectedEvent(null); setLanes([]); setGenderFilter(null); }}
                  className="px-2 py-1.5 rounded-lg text-[10px] font-bold" style={{ backgroundColor: VUKZ.border, color: VUKZ.textLight }}>
                  Trocar
                </button>
              </div>
            ) : (
              <div>
                <input
                  type="text"
                  placeholder="Buscar competição..."
                  value={compSearch}
                  onChange={e => setCompSearch(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-xs border mb-2"
                  style={{ borderColor: VUKZ.border, color: VUKZ.text }}
                  autoFocus
                />
                {loadingComps ? (
                  <div className="flex items-center gap-2 py-3 justify-center text-xs" style={{ color: VUKZ.textLight }}>
                    <RefreshCw size={12} className="animate-spin" /> Carregando...
                  </div>
                ) : (
                  <div className="max-h-40 overflow-y-auto rounded-lg border" style={{ borderColor: VUKZ.border }}>
                    {filteredComps.length === 0 && (
                      <div className="p-3 text-xs text-center" style={{ color: VUKZ.textLight }}>Nenhuma competição encontrada</div>
                    )}
                    {filteredComps.slice(0, 30).map(c => (
                      <button key={c.id}
                        onClick={() => { setSelectedComp(c); setSelectedEvent(null); setLanes([]); setGenderFilter(null); }}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 transition-colors border-b last:border-0 flex items-center justify-between"
                        style={{ borderColor: VUKZ.border + '80' }}>
                        <span className="font-bold truncate" style={{ color: VUKZ.text }}>{c.name}</span>
                        <span className="text-[10px] shrink-0 ml-2" style={{ color: VUKZ.textLight }}>{c.year}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Step 2: Gender filter */}
          {selectedComp && !loadingEvents && events.length > 0 && (
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider mb-1.5 block" style={{ color: VUKZ.textLight }}>
                2. Sexo
              </label>
              <div className="flex gap-1.5">
                {[
                  { value: null, label: 'Todos' },
                  { value: 'M', label: 'Masculino' },
                  { value: 'F', label: 'Feminino' },
                ].map(g => (
                  <button key={g.label}
                    onClick={() => { setGenderFilter(g.value); setSelectedEvent(null); setLanes([]); }}
                    className="px-4 py-1.5 rounded-lg text-[11px] font-bold transition-all"
                    style={{
                      backgroundColor: genderFilter === g.value ? VUKZ.primary : '#f3f4f6',
                      color: genderFilter === g.value ? 'white' : VUKZ.text,
                      border: `1px solid ${genderFilter === g.value ? VUKZ.primary : VUKZ.border}`,
                    }}>
                    {g.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Event (Prova) */}
          {selectedComp && (
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider mb-1.5 block" style={{ color: VUKZ.textLight }}>
                3. Prova / Evento
              </label>
              {loadingEvents ? (
                <div className="flex items-center gap-2 py-3 justify-center text-xs" style={{ color: VUKZ.textLight }}>
                  <RefreshCw size={12} className="animate-spin" /> Carregando provas...
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-48 overflow-y-auto">
                  {events.filter(ev => !genderFilter || ev.gender === genderFilter).map(ev => (
                    <button key={ev.event_id}
                      onClick={() => { setSelectedEvent(ev); setSelectedPhase(ev.phases?.includes('Final') ? 'Final' : ev.phases?.[0] || 'Final'); }}
                      className="px-3 py-2 rounded-lg text-[11px] font-bold text-left transition-all"
                      style={{
                        backgroundColor: selectedEvent?.event_id === ev.event_id ? VUKZ.primary : '#f3f4f6',
                        color: selectedEvent?.event_id === ev.event_id ? 'white' : VUKZ.text,
                        border: `1px solid ${selectedEvent?.event_id === ev.event_id ? VUKZ.primary : VUKZ.border}`,
                      }}>
                      {ev.event_name}
                      {ev.phases && (
                        <span className="block text-[9px] font-normal mt-0.5" style={{ opacity: 0.7 }}>
                          {ev.phases.join(', ')}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 4: Phase */}
          {selectedEvent && availablePhases.length > 1 && (
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider mb-1.5 block" style={{ color: VUKZ.textLight }}>
                4. Fase
              </label>
              <div className="flex gap-1.5">
                {availablePhases.map(p => (
                  <button key={p}
                    onClick={() => setSelectedPhase(p)}
                    className="px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all"
                    style={{
                      backgroundColor: selectedPhase === p ? VUKZ.primary : '#f3f4f6',
                      color: selectedPhase === p ? 'white' : VUKZ.text,
                      border: `1px solid ${selectedPhase === p ? VUKZ.primary : VUKZ.border}`,
                    }}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 5: Lanes (auto-loaded) */}
          {selectedEvent && (
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider mb-1.5 block" style={{ color: VUKZ.textLight }}>
                {availablePhases.length > 1 ? '5' : '4'}. Raias da Prova
              </label>
              {loadingLanes ? (
                <div className="flex items-center gap-2 py-3 justify-center text-xs" style={{ color: VUKZ.textLight }}>
                  <RefreshCw size={12} className="animate-spin" /> Carregando raias...
                </div>
              ) : lanes.length > 0 ? (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px]" style={{ color: VUKZ.textLight }}>
                      {selectedLaneIds.size === 0
                        ? 'Clique para selecionar atleta(s) — ou vincule todos'
                        : `${selectedLaneIds.size} atleta${selectedLaneIds.size > 1 ? 's' : ''} selecionado${selectedLaneIds.size > 1 ? 's' : ''}`}
                    </span>
                    {selectedLaneIds.size > 0 && (
                      <button onClick={() => setSelectedLaneIds(new Set())}
                        className="text-[10px] font-bold px-2 py-0.5 rounded-md transition-all hover:opacity-80"
                        style={{ color: VUKZ.textLight, backgroundColor: VUKZ.border }}>
                        Limpar seleção
                      </button>
                    )}
                  </div>
                  <div className="rounded-xl border overflow-hidden" style={{ borderColor: VUKZ.border }}>
                    <table className="w-full text-xs">
                      <thead>
                        <tr style={{ backgroundColor: '#f9fafb' }}>
                          <th className="px-2 py-2 text-center font-bold" style={{ color: VUKZ.textLight, width: 32 }}></th>
                          <th className="px-3 py-2 text-left font-bold" style={{ color: VUKZ.textLight }}>Raia</th>
                          <th className="px-3 py-2 text-left font-bold" style={{ color: VUKZ.textLight }}>Atleta</th>
                          <th className="px-3 py-2 text-left font-bold" style={{ color: VUKZ.textLight }}>Clube</th>
                          <th className="px-3 py-2 text-right font-bold" style={{ color: VUKZ.textLight }}>Tempo</th>
                          <th className="px-3 py-2 text-right font-bold" style={{ color: VUKZ.textLight }}>Reação</th>
                          <th className="px-3 py-2 text-center font-bold" style={{ color: VUKZ.textLight }}>Pos</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lanes.map((l, i) => {
                          const isSelected = selectedLaneIds.has(l.lane);
                          return (
                            <tr key={i}
                              onClick={() => toggleLane(l.lane)}
                              className="border-t cursor-pointer transition-all"
                              style={{
                                borderColor: VUKZ.border + '60',
                                backgroundColor: isSelected ? `${VUKZ.primary}12` : 'transparent',
                              }}>
                              <td className="px-2 py-2 text-center">
                                <div className="w-4 h-4 rounded border-2 flex items-center justify-center mx-auto transition-all"
                                  style={{
                                    borderColor: isSelected ? VUKZ.primary : VUKZ.border,
                                    backgroundColor: isSelected ? VUKZ.primary : 'white',
                                  }}>
                                  {isSelected && (
                                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                                      <path d="M2 5L4 7L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-2 font-bold" style={{ color: VUKZ.text }}>{l.lane}</td>
                              <td className="px-3 py-2 font-bold" style={{ color: isSelected ? VUKZ.primary : VUKZ.text }}>{l.athlete_name}</td>
                              <td className="px-3 py-2 text-[10px]" style={{ color: VUKZ.textLight }}>{l.club_name || '—'}</td>
                              <td className="px-3 py-2 text-right font-mono font-bold" style={{ color: VUKZ.primary }}>
                                {l.time_formatted || fmtTime(l.time_seconds)}
                              </td>
                              <td className="px-3 py-2 text-right font-mono text-[10px]" style={{ color: VUKZ.textLight }}>
                                {l.reaction_time != null ? `${l.reaction_time.toFixed(2)}s` : '—'}
                              </td>
                              <td className="px-3 py-2 text-center">
                                {l.position === 1 ? (
                                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>🥇</span>
                                ) : l.position != null ? (
                                  <span className="text-[10px] font-bold" style={{ color: VUKZ.textLight }}>{l.position}º</span>
                                ) : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="p-4 rounded-xl text-center text-xs" style={{ backgroundColor: '#fffbeb', color: '#92400e' }}>
                  <AlertTriangle size={14} className="inline mr-1" />
                  Nenhuma raia encontrada para esta prova/fase.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t" style={{ borderColor: VUKZ.border }}>
          <span className="text-[10px]" style={{ color: VUKZ.textLight }}>
            {selectedComp && selectedEvent
              ? selectedLaneIds.size > 0
                ? `${selectedLaneIds.size} atleta${selectedLaneIds.size > 1 ? 's' : ''} selecionado${selectedLaneIds.size > 1 ? 's' : ''} · Tempos oficiais serão usados`
                : `${lanes.length} raias · Selecione atleta(s) ou vincule todos`
              : 'Selecione competição e prova para vincular'}
          </span>
          <div className="flex gap-2">
            <button onClick={onClose}
              className="px-4 py-2 rounded-lg text-xs font-bold" style={{ backgroundColor: VUKZ.border, color: VUKZ.textLight }}>
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={!selectedComp || !selectedEvent}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-xs font-bold transition-all disabled:opacity-40"
              style={{ backgroundColor: VUKZ.primary, color: 'white' }}>
              <Link2 size={13} />
              {selectedLaneIds.size > 0 ? `Vincular (${selectedLaneIds.size})` : 'Vincular Todos'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Events Tab — Stroke events list with timestamps, confidence, type
// ═══════════════════════════════════════════════════════════════════════════════

const EventsTab = ({ metrics, apiData, debugOverlay, debugLoading, onGenerateDebug, overlayMode, onOverlayModeChange }) => {
  // Extract stroke events from apiData or debugOverlay
  const events = React.useMemo(() => {
    // Priority 1: debug overlay timeline
    if (debugOverlay?.timeline?.events?.length) {
      return debugOverlay.timeline.events.map((e, i) => ({
        index: i + 1,
        frame: e.frame ?? '—',
        time_s: e.time_s != null ? e.time_s.toFixed(2) : '—',
        type: e.type || 'stroke',
        label: e.label || (e.type === 'peak' ? '⬆ Braçada (pico)' : e.type === 'trough' ? '⬇ Vale' : '🏊 Stroke'),
        confidence: e.confidence != null ? (e.confidence * 100).toFixed(0) + '%' : '—',
        keypoint: e.keypoint || '—',
        signal_value: e.signal_value != null ? e.signal_value.toFixed(2) : '—',
      }));
    }
    // Priority 2: stroke_counting cycles from apiData
    if (apiData?.stroke_counting?.cycles?.length) {
      return apiData.stroke_counting.cycles.map((c, i) => ({
        index: i + 1,
        frame: c.peak_frame ?? '—',
        time_s: c.peak_frame && apiData.fps ? (c.peak_frame / apiData.fps).toFixed(2) : '—',
        type: 'stroke_cycle',
        label: `🏊 Ciclo #${i + 1}`,
        confidence: c.confidence != null ? (c.confidence * 100).toFixed(0) + '%' : '—',
        keypoint: c.keypoint || apiData.stroke_counting?.dominant_signal || '—',
        signal_value: c.amplitude != null ? c.amplitude.toFixed(2) : '—',
      }));
    }
    // Priority 3: basic stroke count
    if (metrics?.strokeCount) {
      return Array.from({ length: metrics.strokeCount }, (_, i) => ({
        index: i + 1,
        frame: '—',
        time_s: '—',
        type: 'estimated',
        label: `🏊 Braçada #${i + 1} (estimada)`,
        confidence: '—',
        keypoint: '—',
        signal_value: '—',
      }));
    }
    return [];
  }, [debugOverlay, apiData, metrics]);

  const stats = debugOverlay?.stats || null;

  return (
    <div className="space-y-4">
      {/* Header with generate button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold" style={{ color: VUKZ.text }}>
            <Target size={18} className="inline mr-2" style={{ color: VUKZ.primary }} />
            Eventos de Braçada
          </h3>
          <p className="text-sm mt-1" style={{ color: VUKZ.textLight }}>
            {events.length} evento(s) detectado(s) pela IA
          </p>
        </div>
        {!debugOverlay && (
          <div className="flex items-center gap-3">
            {/* Overlay mode selector */}
            <select
              value={overlayMode}
              onChange={(e) => onOverlayModeChange(e.target.value)}
              className="px-3 py-2 rounded-lg text-sm font-medium border"
              style={{ borderColor: VUKZ.border, color: VUKZ.text, background: '#fff' }}
            >
              <option value="clean">Limpo</option>
              <option value="technical">Técnico</option>
              <option value="forensic">Forense</option>
            </select>
            <button
              onClick={onGenerateDebug}
              disabled={debugLoading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all"
              style={{
                background: debugLoading ? VUKZ.border : VUKZ.primary,
                color: '#fff',
                opacity: debugLoading ? 0.7 : 1,
              }}
            >
              {debugLoading ? (
                <><RotateCcw size={14} className="animate-spin" /> Gerando...</>
              ) : (
                <><Sparkles size={14} /> Gerar Debug</>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Stats summary (when debug overlay available) */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total Braçadas', value: stats.total_strokes ?? events.length, icon: Waves },
            { label: 'Confiança Média', value: stats.avg_confidence != null ? (stats.avg_confidence * 100).toFixed(0) + '%' : '—', icon: Shield },
            { label: 'Sinal Dominante', value: stats.dominant_signal || '—', icon: Activity },
            { label: 'Duração', value: stats.duration_s != null ? stats.duration_s.toFixed(1) + 's' : '—', icon: Timer },
          ].map((s, i) => (
            <div key={i} className="rounded-xl p-3 text-center" style={{ background: VUKZ.cardBg, border: `1px solid ${VUKZ.border}` }}>
              <s.icon size={16} className="mx-auto mb-1" style={{ color: VUKZ.primary }} />
              <div className="text-xl font-bold" style={{ color: VUKZ.text }}>{s.value}</div>
              <div className="text-xs" style={{ color: VUKZ.textLight }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Events table */}
      {events.length > 0 ? (
        <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${VUKZ.border}` }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: VUKZ.primary + '15' }}>
                  {['#', 'Evento', 'Frame', 'Tempo', 'Keypoint', 'Sinal', 'Confiança'].map((h) => (
                    <th key={h} className="px-3 py-2 text-left font-semibold" style={{ color: VUKZ.text, borderBottom: `1px solid ${VUKZ.border}` }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {events.map((ev) => (
                  <tr key={ev.index} className="hover:bg-opacity-50" style={{ borderBottom: `1px solid ${VUKZ.border}20` }}>
                    <td className="px-3 py-2 font-mono text-xs" style={{ color: VUKZ.textLight }}>{ev.index}</td>
                    <td className="px-3 py-2 font-medium" style={{ color: VUKZ.text }}>{ev.label}</td>
                    <td className="px-3 py-2 font-mono text-xs" style={{ color: VUKZ.textLight }}>{ev.frame}</td>
                    <td className="px-3 py-2 font-mono" style={{ color: VUKZ.primary }}>{ev.time_s}s</td>
                    <td className="px-3 py-2 text-xs" style={{ color: VUKZ.textLight }}>{ev.keypoint}</td>
                    <td className="px-3 py-2 font-mono text-xs" style={{ color: VUKZ.textLight }}>{ev.signal_value}</td>
                    <td className="px-3 py-2">
                      {ev.confidence !== '—' && (
                        <span
                          className="px-2 py-0.5 rounded-full text-xs font-semibold"
                          style={{
                            background: parseFloat(ev.confidence) >= 70 ? '#10b98120' : parseFloat(ev.confidence) >= 40 ? '#f59e0b20' : '#ef444420',
                            color: parseFloat(ev.confidence) >= 70 ? '#10b981' : parseFloat(ev.confidence) >= 40 ? '#f59e0b' : '#ef4444',
                          }}
                        >
                          {ev.confidence}
                        </span>
                      )}
                      {ev.confidence === '—' && <span style={{ color: VUKZ.textLight }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-center py-12 rounded-xl" style={{ background: VUKZ.cardBg, border: `1px solid ${VUKZ.border}` }}>
          <Target size={40} className="mx-auto mb-3" style={{ color: VUKZ.textLight }} />
          <p className="text-sm" style={{ color: VUKZ.textLight }}>Nenhum evento detectado ainda</p>
          <p className="text-xs mt-1" style={{ color: VUKZ.textLight }}>Execute a análise e gere o debug para ver eventos detalhados</p>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Signal Debug Tab — Signal chart with peaks/troughs + trajectory
// ═══════════════════════════════════════════════════════════════════════════════

const SignalDebugTab = ({ debugOverlay, debugLoading, apiData, onGenerateDebug, overlayMode, onOverlayModeChange }) => {
  const [selectedSignal, setSelectedSignal] = useState(0);

  // Build signal chart data from debugOverlay or apiData
  const signalData = React.useMemo(() => {
    // From debug overlay
    if (debugOverlay?.signal?.signals?.length) {
      return debugOverlay.signal.signals;
    }
    // From apiData stroke_counting debug_info
    if (apiData?.stroke_counting?.debug_info) {
      const dbg = apiData.stroke_counting.debug_info;
      const signals = [];
      // Build from trajectory + peaks
      if (dbg.trajectory?.length) {
        signals.push({
          name: dbg.signal_type || 'trajectory',
          keypoint: dbg.keypoint || 'unknown',
          values: dbg.trajectory.map((v, i) => ({ frame: i, value: v })),
          peaks: (dbg.peaks || []).map((p) => ({ frame: p, value: dbg.trajectory[p] ?? 0 })),
          troughs: (dbg.troughs || []).map((t) => ({ frame: t, value: dbg.trajectory[t] ?? 0 })),
        });
      }
      return signals;
    }
    return [];
  }, [debugOverlay, apiData]);

  const currentSignal = signalData[selectedSignal] || null;

  // Merge values + peak/trough markers for recharts
  const chartData = React.useMemo(() => {
    if (!currentSignal?.values?.length) return [];
    const peakSet = new Set((currentSignal.peaks || []).map((p) => p.frame));
    const troughSet = new Set((currentSignal.troughs || []).map((t) => t.frame));
    return currentSignal.values.map((pt) => ({
      frame: pt.frame,
      value: pt.value,
      isPeak: peakSet.has(pt.frame),
      isTrough: troughSet.has(pt.frame),
    }));
  }, [currentSignal]);

  // Custom dot renderer for peaks and troughs
  const renderDot = (props) => {
    const { cx, cy, payload } = props;
    if (payload.isPeak) {
      return <circle cx={cx} cy={cy} r={5} fill="#10b981" stroke="#fff" strokeWidth={2} />;
    }
    if (payload.isTrough) {
      return <circle cx={cx} cy={cy} r={4} fill="#f59e0b" stroke="#fff" strokeWidth={2} />;
    }
    return null;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold" style={{ color: VUKZ.text }}>
            <Cpu size={18} className="inline mr-2" style={{ color: VUKZ.primary }} />
            Debug de Sinal
          </h3>
          <p className="text-sm mt-1" style={{ color: VUKZ.textLight }}>
            Trajetória temporal dos keypoints com picos (braçadas) e vales marcados
          </p>
        </div>
        {!debugOverlay && (
          <button
            onClick={onGenerateDebug}
            disabled={debugLoading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all"
            style={{
              background: debugLoading ? VUKZ.border : VUKZ.primary,
              color: '#fff',
              opacity: debugLoading ? 0.7 : 1,
            }}
          >
            {debugLoading ? (
              <><RotateCcw size={14} className="animate-spin" /> Gerando...</>
            ) : (
              <><Sparkles size={14} /> Gerar Debug</>
            )}
          </button>
        )}
      </div>

      {/* Signal selector (when multiple signals) */}
      {signalData.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          {signalData.map((sig, i) => (
            <button
              key={i}
              onClick={() => setSelectedSignal(i)}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
              style={{
                background: selectedSignal === i ? VUKZ.primary : VUKZ.cardBg,
                color: selectedSignal === i ? '#fff' : VUKZ.textLight,
                border: `1px solid ${selectedSignal === i ? VUKZ.primary : VUKZ.border}`,
              }}
            >
              {sig.name} ({sig.keypoint})
            </button>
          ))}
        </div>
      )}

      {/* Signal chart */}
      {chartData.length > 0 ? (
        <div className="rounded-xl p-4" style={{ background: VUKZ.cardBg, border: `1px solid ${VUKZ.border}` }}>
          {/* Legend */}
          <div className="flex items-center gap-4 mb-3 text-xs" style={{ color: VUKZ.textLight }}>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full inline-block" style={{ background: VUKZ.primary }} />
              Sinal ({currentSignal?.keypoint || '?'})
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full inline-block" style={{ background: '#10b981' }} />
              Pico (braçada)
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full inline-block" style={{ background: '#f59e0b' }} />
              Vale
            </span>
            <span className="ml-auto font-mono">
              {currentSignal?.peaks?.length || 0} picos · {currentSignal?.troughs?.length || 0} vales · {chartData.length} frames
            </span>
          </div>

          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={VUKZ.border} />
              <XAxis
                dataKey="frame"
                tick={{ fontSize: 10, fill: VUKZ.textLight }}
                label={{ value: 'Frame', position: 'insideBottomRight', offset: -5, fontSize: 11, fill: VUKZ.textLight }}
              />
              <YAxis
                tick={{ fontSize: 10, fill: VUKZ.textLight }}
                label={{ value: 'Posição (px)', angle: -90, position: 'insideLeft', fontSize: 11, fill: VUKZ.textLight }}
              />
              <Tooltip
                contentStyle={{
                  background: VUKZ.cardBg,
                  border: `1px solid ${VUKZ.border}`,
                  borderRadius: '8px',
                  fontSize: 12,
                }}
                formatter={(value, name) => [value?.toFixed(2), 'Valor']}
                labelFormatter={(label) => `Frame ${label}`}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke={VUKZ.primary}
                strokeWidth={1.5}
                dot={renderDot}
                activeDot={{ r: 6, stroke: VUKZ.primary, strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>

          {/* Signal stats */}
          {currentSignal && (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-3">
              {[
                { label: 'Tipo Sinal', value: currentSignal.name },
                { label: 'Keypoint', value: currentSignal.keypoint },
                { label: 'Picos', value: currentSignal.peaks?.length || 0 },
                { label: 'Vales', value: currentSignal.troughs?.length || 0 },
                { label: 'Frames', value: currentSignal.values?.length || 0 },
              ].map((s, i) => (
                <div key={i} className="rounded-lg p-2 text-center" style={{ background: VUKZ.primary + '08', border: `1px solid ${VUKZ.border}` }}>
                  <div className="text-sm font-bold" style={{ color: VUKZ.text }}>{s.value}</div>
                  <div className="text-xs" style={{ color: VUKZ.textLight }}>{s.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-12 rounded-xl" style={{ background: VUKZ.cardBg, border: `1px solid ${VUKZ.border}` }}>
          <Cpu size={40} className="mx-auto mb-3" style={{ color: VUKZ.textLight }} />
          <p className="text-sm" style={{ color: VUKZ.textLight }}>Nenhum dado de sinal disponível</p>
          <p className="text-xs mt-1" style={{ color: VUKZ.textLight }}>
            Clique em "Gerar Debug" para extrair a trajetória temporal dos keypoints
          </p>
          {!debugOverlay && (
            <button
              onClick={onGenerateDebug}
              disabled={debugLoading}
              className="mt-4 px-5 py-2 rounded-lg font-semibold text-sm"
              style={{ background: VUKZ.primary, color: '#fff' }}
            >
              {debugLoading ? 'Gerando...' : '⚡ Gerar Debug de Sinal'}
            </button>
          )}
        </div>
      )}

      {/* Raw JSON accordion (dev tool) */}
      {debugOverlay && (
        <details className="rounded-xl overflow-hidden" style={{ border: `1px solid ${VUKZ.border}` }}>
          <summary className="px-4 py-2 cursor-pointer text-xs font-mono" style={{ background: VUKZ.primary + '08', color: VUKZ.textLight }}>
            📋 Raw Debug JSON
          </summary>
          <pre className="p-4 text-xs overflow-auto max-h-64 font-mono" style={{ background: VUKZ.cardBg, color: VUKZ.textLight }}>
            {JSON.stringify(debugOverlay, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════════
// Helper: Build competition summary from regular biomechanics data
// ═══════════════════════════════════════════════════════════════════════════════
function _buildCompSummaryFromBio(result) {
  if (!result?.biomechanics) return null;
  const bio = result.biomechanics;
  const stroke = bio.stroke || {};
  const turns = bio.turn_detection;
  const splits = bio.split_analysis;
  const count = stroke.count || stroke.stroke_count || 0;
  const ratePerMin = stroke.rate_per_min || stroke.stroke_rate || 0;
  const race = result.race_analysis;
  const distance = race?.race_distance_m || 50;
  const dps = count > 0 ? distance / count : 0;
  const spm = distance > 0 ? count / distance : 0;

  const builtSplits = (splits?.splits || []).map((s, i) => ({
    label: s.distance_label || `${s.split_number * 50}m`,
    time_s: s.split_time_s,
    cumulative_s: s.cumulative_time_s,
    strokes: s.strokes || Math.round(count / (splits?.splits?.length || 1)),
    stroke_rate: ratePerMin,
    strokes_per_meter: spm,
    avg_velocity_ms: s.avg_velocity_ms || 0,
  }));

  const builtTurns = (turns?.turns || []).map(t => ({
    turn_number: t.turn_number,
    at_distance_m: t.turn_number * (race?.pool_length_m || 50),
    time_s: t.turn_time_s,
    wall_time_s: t.wall_time_s || 0,
    confidence: 0.5,
  }));

  return {
    stroke_count_total: count,
    stroke_rate_avg: ratePerMin,
    dps_m: dps,
    strokes_per_meter: spm,
    splits: builtSplits,
    turns: builtTurns,
    pacing_type: splits?.pacing_type || race?.pacing_type || 'unknown',
    warnings: [],
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// Helper: Build debug overlay data locally from pipeline apiData
// Used as fallback when backend /debug/generate is unreachable or session_id missing
// ═══════════════════════════════════════════════════════════════════════════════
function _buildLocalDebugOverlay(apiData) {
  if (!apiData?.biomechanics) return null;
  const bio = apiData.biomechanics;
  const stroke = bio.stroke || {};
  const race = apiData.race_analysis;

  // Build stroke events timeline from available data
  const events = [];
  const strokeFrames = stroke.stroke_frames || stroke.peak_frames || [];
  strokeFrames.forEach((frame, i) => {
    events.push({
      frame,
      time_s: frame / 30, // approximate at 30fps
      type: 'stroke',
      label: `Stroke ${i + 1}`,
    });
  });

  // Add turn events if available
  const turns = bio.turn_detection?.turns || [];
  turns.forEach(t => {
    events.push({
      frame: t.frame || Math.round((t.turn_time_s || 0) * 30),
      time_s: t.turn_time_s || 0,
      type: 'turn',
      label: `Turn ${t.turn_number || ''}`,
    });
  });

  events.sort((a, b) => a.frame - b.frame);

  // Build signal data from stroke info
  const signalData = {
    signal_type: stroke.signal_type || 'wrist_y',
    values: stroke.signal_values || stroke.raw_signal || [],
    peaks: strokeFrames,
    fps: race?.fps || stroke.fps || 30,
    useful_window: stroke.useful_window || null,
  };

  return {
    session_id: apiData.session_id || 'local',
    events,
    signal: signalData,
    summary: {
      stroke_count: stroke.count || stroke.stroke_count || 0,
      stroke_rate: stroke.rate_per_min || stroke.stroke_rate || 0,
      turn_count: turns.length,
      total_frames: race?.total_frames || 0,
    },
    source: 'local_fallback',
  };
}

export default function VideoAnalysisPage() {
  const navigate = useNavigate();
  // ── State ───────────────────────────────────────────────────────
  const [videoFile, setVideoFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [bestModel, setBestModel] = useState(null);
  const [medleyReady, setMedleyReady] = useState(null);
  const [selectedLanes, setSelectedLanes] = useState([]);
  const [laneAthletes, setLaneAthletes] = useState({}); // { laneNumber: 'Athlete Name' }
  const [swimStyle, setSwimStyle] = useState('freestyle');
  const [raceDistance, setRaceDistance] = useState(50);
  const [videoMode, setVideoMode] = useState('competition'); // 'competition' | 'training'
  const [scanMode, setScanMode] = useState(false);        // true = scan lanes first
  const [laneScanResult, setLaneScanResult] = useState(null); // PoolScanResult from worker
  const [scanSessionId, setScanSessionId] = useState(null);   // session ID during scan
  const [showLinkRaceModal, setShowLinkRaceModal] = useState(false);  // Link Race modal
  const [raceLink, setRaceLink] = useState(null); // pre-analysis race link: {competition_id, competition_name, event_id, event_name, phase, lanes}
  const { convertToMp4, converting, progress: convertProgress, statusText: convertStatus } = useVideoConverter();
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const [analysisState, setAnalysisState] = useState('idle'); // idle | analyzing | scanned | timeout | done
  const [analysisStage, setAnalysisStage] = useState(0);
  const [pendingSessionId, setPendingSessionId] = useState(null); // session ID for timeout resume
  const [metrics, setMetrics] = useState(null);

  // ── Backend API response state ──
  const [apiData, setApiData] = useState(null);        // Full response from /upload-and-analyze (REAL pipeline)
  const [showOverlay, setShowOverlay] = useState(true); // Skeleton overlay toggle
  const [videoSize, setVideoSize] = useState({ w: 640, h: 360 });
  const [uploadProgress, setUploadProgress] = useState(0); // Upload progress 0-100

  const [activeTab, setActiveTab] = useState('metrics'); // metrics | angles | splits | events | timing | dashboard | insights
  const [debugOverlay, setDebugOverlay] = useState(null); // {timeline, signal, stats, debug_id}
  const [debugLoading, setDebugLoading] = useState(false);
  const [overlayMode, setOverlayMode] = useState('clean'); // clean | technical | forensic
  const [showAnnotatedVideo, setShowAnnotatedVideo] = useState(false); // Original / Annotated toggle
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      text: 'Olá! Sou o Swim Vision AI — seu analista de biomecânica aquática.\n\nFaça upload de um vídeo de natação para que eu possa detectar articulações, mapear ângulos e gerar insights de performance.',
      confidence: null,
      showFeedback: false,
    },
  ]);
  const [inputMsg, setInputMsg] = useState('');

  const [dtModalOpen, setDtModalOpen] = useState(false);
  const [expandedVideo, setExpandedVideo] = useState(false);
  const [showTrimmer, setShowTrimmer] = useState(false);
  const [clipProcessing, setClipProcessing] = useState(false);

  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const chatEndRef = useRef(null);

  // ── Derived: current keypoint frame not available in direct pipeline ──
  const currentFrame = null;  // Real pipeline returns aggregated metrics, not per-frame poses

  // ── Annotated video: use blob URL when toggle is ON and URL exists ──
  const annotatedVideoUrl = apiData?.annotatedVideoUrl || null;
  const activeVideoSrc = (showAnnotatedVideo && annotatedVideoUrl) ? annotatedVideoUrl : videoUrl;

  // ── Effects ─────────────────────────────────────────────────────
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Track video element size for overlay
  useEffect(() => {
    const updateSize = () => {
      if (videoRef.current) {
        setVideoSize({ w: videoRef.current.clientWidth, h: videoRef.current.clientHeight });
      }
    };
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  // Load best model info on mount
  useEffect(() => {
    getBestModel().then(({ data }) => {
      if (data?.success) setBestModel(data.data);
    }).catch(() => {});
    checkMedleyReadiness().then(({ data }) => {
      if (data?.success) setMedleyReady(data.data);
    }).catch(() => {});
  }, []);



  // ── Video Handlers ──────────────────────────────────────────────
  const handleFileSelect = async (e) => {
    let file = e.target.files?.[0];
    if (!file) return;

    // Convert unsupported formats (MTS, MKV, AVI) to MP4 in-browser
    if (needsConversion(file)) {
      pushMsg('assistant', '\u23f3 Convertendo ' + file.name + ' para MP4...', null, false);
      try {
        file = await convertToMp4(file);
        pushMsg('assistant', '\u2705 Conversao concluida: ' + file.name + ' (' + (file.size / 1024 / 1024).toFixed(1) + ' MB)', null, false);
      } catch (err) {
        pushMsg('assistant', '\u274c Erro na conversao: ' + err.message, null, false);
        return;
      }
    }

    setVideoFile(file);
    setVideoUrl(URL.createObjectURL(file));
    setMetrics(null);
    setAnalysisState('idle');
    setShowAnnotatedVideo(false);
    pushMsg('assistant', `✓ Vídeo "${file.name}" carregado (${(file.size / 1024 / 1024).toFixed(1)} MB).\n\nClique em "Analisar com IA" para iniciar a detecção de movimentos.`, null, false);
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) { videoRef.current.play(); setIsPlaying(true); }
    else { videoRef.current.pause(); setIsPlaying(false); }
  };

  const changeSpeed = (s) => {
    setPlaybackSpeed(s);
    if (videoRef.current) videoRef.current.playbackRate = s;
  };

  const skipFrames = (dir) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime += dir * (1 / 30); // 30fps frame skip
  };

  // Refs for direct DOM updates (avoids full re-render on every timeupdate)
  const timeDisplayRef = useRef(null);
  const progressBarRef = useRef(null);
  const progressDotRef = useRef(null);
  const lastTimeUpdateRef = useRef(0);

  const handleTimeUpdate = useCallback(() => {
    if (!videoRef.current) return;
    const t = videoRef.current.currentTime;
    const now = performance.now();
    
    // Direct DOM updates (no React re-render) — ~60fps capable
    if (timeDisplayRef.current) {
      const m = Math.floor(t / 60);
      const sec = Math.floor(t % 60);
      const ms = Math.floor((t % 1) * 100);
      timeDisplayRef.current.textContent = `${m}:${String(sec).padStart(2, '0')}.${String(ms).padStart(2, '0')}`;
    }
    if (progressBarRef.current && duration > 0) {
      const pct = (t / duration) * 100;
      progressBarRef.current.style.width = `${pct}%`;
      if (progressDotRef.current) progressDotRef.current.style.left = `${pct}%`;
    }

    // Throttle React state update to max 4x/sec (for any component that needs currentTime)
    if (now - lastTimeUpdateRef.current > 250) {
      lastTimeUpdateRef.current = now;
      setCurrentTime(t);
    }
  }, [duration]);

  const handleLoadedMetadata = () => {
    if (videoRef.current) setDuration(videoRef.current.duration);
  };

  const seekTo = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    if (videoRef.current) videoRef.current.currentTime = pct * duration;
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    const ms = Math.floor((s % 1) * 100);
    return `${m}:${String(sec).padStart(2, '0')}.${String(ms).padStart(2, '0')}`;
  };

  // ── Lane Scan (detect occupied lanes before analysis) ─────────
  const startLaneScan = async () => {
    if (!videoFile) return;
    setAnalysisState('analyzing');
    setAnalysisStage(0);
    setUploadProgress(0);
    setLaneScanResult(null);

    try {
      pushMsg('assistant', '📤 Enviando vídeo para detecção de raias...', null, false);

      const { data: uploadResp } = await uploadVideoAsync(videoFile, {
        style: swimStyle,
        poolLength: 50,
        raceDistance,
        scanLanes: true,
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        },
      });

      if (!uploadResp?.success) throw new Error(uploadResp?.error || 'Upload failed');

      const sessionId = uploadResp.data.session_id;
      setScanSessionId(sessionId);
      setAnalysisStage(1);
      pushMsg('assistant', `✅ Vídeo recebido! Sessão #${sessionId}.\n🔍 Escaneando raias da piscina... (30-60 segundos)`, null, false);

      const scanData = await pollLaneScanUntilDone(sessionId, {
        interval: 3000,
        timeout: 300000,
        onStatus: (s) => console.log(`[SwimVision] Scan poll: status=${s.status}`),
      });

      setLaneScanResult(scanData);
      setAnalysisState('scanned');

      const occ = (scanData.lanes_detected || []).filter(l => l.occupied);
      const occLanes = occ.map(l => l.lane);
      setSelectedLanes(occLanes); // auto-select occupied lanes

      const laneList = (scanData.lanes_detected || []).map(l => {
        const emoji = l.status === 'occupied' ? '🏊' : (l.status === 'uncertain' ? '❓' : '💧');
        return `  Raia ${l.lane}: ${emoji} ${l.status} (${Math.round(l.confidence * 100)}%)`;
      }).join('\n');

      pushMsg('assistant',
        `🔍 **Scan completo** — ${occ.length} raias ocupadas: [${occLanes.join(', ')}]\n\n${laneList}\n\n` +
        `Selecione as raias desejadas e clique em **"Analisar"**.`,
        null, false);

    } catch (err) {
      console.error('[SwimVision] Scan error:', err);
      setAnalysisState('idle');
      pushMsg('assistant', `❌ Erro no scan: ${err.message}`, null, false);
    }
  };

  // ── Analyze selected lanes (after scan) ─────────────────────
  const analyzeAfterScan = async () => {
    if (!scanSessionId || !selectedLanes.length) return;
    setAnalysisState('analyzing');
    setAnalysisStage(2);

    let result = null;
    try {
      pushMsg('assistant', `🚀 Iniciando análise biomecânica das raias [${selectedLanes.join(', ')}]...`, null, false);

      await analyzeSelectedLanes(scanSessionId, selectedLanes, {
        style: swimStyle,
        raceDistance,
        poolLength: 50,
        laneAthletes: Object.keys(laneAthletes).length > 0 ? laneAthletes : undefined,
      });

      // Navigate to dedicated status page for progress tracking
      pushMsg('assistant', `🚀 Análise iniciada! Acompanhe o progresso na página de status.`, null, false);
      navigate(`/swim-vision/session/${scanSessionId}/status`);
      return; // status page handles polling, cancel, and redirect to results

    } catch (err) {
      console.error('[SwimVision] Post-scan analysis error:', err);
      setAnalysisState('scanned'); // go back to scanned state, not idle
      pushMsg('assistant', `❌ Erro na análise: ${err.message}`, null, false);
    }
  };

  // ── Analysis (Async: upload → queue → poll) ──────────────────
  const startAnalysis = async () => {
    if (!videoFile) return;

    // If scan mode, go through scan flow
    if (scanMode && !laneScanResult) {
      return startLaneScan();
    }
    // If we have scan results and lanes selected, analyze those
    if (scanMode && laneScanResult && scanSessionId) {
      return analyzeAfterScan();
    }

    setAnalysisState('analyzing');
    setAnalysisStage(0);
    setUploadProgress(0);

    let result = null;
    try {
      // Stage 0: Uploading video
      pushMsg('assistant', '📤 Enviando vídeo para análise...', null, false);

      const { data: uploadResp } = await uploadVideoAsync(videoFile, {
        style: swimStyle,
        poolLength: 50,
        raceDistance,
        analysisMode: videoMode,
        targetLanes: selectedLanes?.length ? selectedLanes : undefined,
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        },
      });

      if (!uploadResp?.success) {
        throw new Error(uploadResp?.error || 'Upload failed');
      }

      const sessionId = uploadResp.data.session_id;
      setAnalysisStage(1);

      // Auto-save race context if pre-linked
      if (raceLink) {
        try {
          await setRaceContext(sessionId, {
            competition_id: raceLink.competition_id,
            event_id: raceLink.event_id,
            phase: raceLink.phase,
            lanes: (raceLink.lanes || []).map(l => ({
              lane_number: l.lane,
              athlete_id: l.athlete_id,
              result_id: l.result_id,
            })),
          });
          console.log('[SwimVision] Race context saved for session', sessionId);
          pushMsg('assistant', `🔗 Prova vinculada: **${raceLink.competition_name}** — ${raceLink.event_name} (${raceLink.phase})`, null, false);
        } catch (e) {
          console.error('[SwimVision] Failed to save race context:', e);
        }
      }

      pushMsg('assistant', `✅ Vídeo recebido! Sessão #${sessionId} criada.\n⏳ Processando análise biomecânica... Isso pode levar 3-8 minutos.`, null, false);

      // Brief pause to let user see "upload complete" before polling stages start
      await new Promise(r => setTimeout(r, 800));
      setAnalysisStage(2);

      // Stages 2-6: Poll until done, advance stages based on elapsed time
      // Pacing: more granular for the first 2 minutes (where most action happens)
      const pollStart = Date.now();
      const stageTimings = [0, 0, 0, 10000, 30000, 90000, 180000]; // stages 0-1 already done

      const sessionResult = await pollSessionUntilDone(sessionId, {
        interval: 4000,
        timeout: 600000,
        onStatus: (session) => {
          const elapsed = Date.now() - pollStart;
          // Advance stage indicator based on elapsed time
          const newStage = stageTimings.findIndex((t, i) => {
            const next = stageTimings[i + 1];
            return next ? elapsed < next : true;
          });
          if (newStage >= 0 && newStage < ANALYSIS_STAGES.length) {
            setAnalysisStage(newStage);
          }
          console.log(`[SwimVision] Poll: status=${session.status}, elapsed=${Math.round(elapsed/1000)}s`);
        },
      });

      // Handle soft timeout — analysis still running on worker
      if (sessionResult?._timedOut) {
        setPendingSessionId(sessionId);
        pushMsg('assistant', `⏳ A análise da sessão #${sessionId} ainda está processando no servidor.\nIsso é normal para vídeos longos ou quando o sistema está sob carga.\n\nClique em "Verificar Status" para atualizar.`, null, false);
        setAnalysisState('timeout');
        return;
      }

      // Session completed — extract result
      // The session object from GET /sessions/:id has the full report when status=completed
      if (sessionResult.full_report_json) {
        try {
          const report = typeof sessionResult.full_report_json === 'string'
            ? JSON.parse(sessionResult.full_report_json)
            : sessionResult.full_report_json;
          result = {
            session_id: sessionId,
            biomechanics: report.biomechanics || report,
            race_analysis: report.race_analysis || null,
            sanity: report.sanity || null,
            annotatedVideoUrl: sessionResult.annotatedVideoUrl || null,
            strokeChartUrl: sessionResult.strokeChartUrl || null,
            strokeTiming: sessionResult.strokeTiming || null,
            // Timing report scalars (Etapa A)
            strokeTimesSeconds: sessionResult.strokeTimesSeconds || null,
            strokeIntervalsSeconds: sessionResult.strokeIntervalsSeconds || null,
            startToFirstStrokeSeconds: sessionResult.startToFirstStrokeSeconds ?? null,
            largestGapSeconds: sessionResult.largestGapSeconds ?? null,
            raceDurationSeconds: sessionResult.raceDurationSeconds ?? null,
            // Official race link (Etapa B)
            officialResultId: sessionResult.officialResultId || null,
            reactionTimeOfficial: sessionResult.reactionTimeOfficial ?? null,
            totalTimeOfficial: sessionResult.totalTimeOfficial ?? null,
            splitsOfficial: sessionResult.splitsOfficial || null,
            pipeline: {
              processing_time_s: (report.processing_time_ms || 0) / 1000,
              model_version: sessionResult.model_version || 'swimxyz',
              stroke_counter_version: sessionResult.stroke_counter_version || report?.stroke_counting?.stroke_counter_version || '—',
              device: sessionResult.device_used || 'cpu',
              data_source: 'real',
            },
          };
        } catch (parseErr) {
          console.warn('[SwimVision] Could not parse full_report_json:', parseErr);
        }
      }

      // Fallback: build result from session summary fields
      if (!result && sessionResult.overall_score != null) {
        result = {
          session_id: sessionId,
          biomechanics: {
            overall_score: sessionResult.overall_score,
            stroke: {
              count: sessionResult.stroke_count,
              rate_per_min: sessionResult.stroke_rate,
              stroke_count: sessionResult.stroke_count,
              stroke_rate: sessionResult.stroke_rate,
            },
          },
          pipeline: {
            processing_time_s: (sessionResult.processing_time_ms || 0) / 1000,
            model_version: sessionResult.model_version || 'swimxyz',
            stroke_counter_version: sessionResult.stroke_counter_version || '—',
            device: sessionResult.device_used || 'cpu',
            data_source: 'real',
          },
        };
      }

      setAnalysisStage(ANALYSIS_STAGES.length - 1);

      // Show "all stages complete" for 1.5s before navigating — gives visual feedback
      await new Promise(r => setTimeout(r, 1500));

    } catch (err) {
      console.error('[SwimVision] Async analysis failed:', err.message);
      pushMsg('assistant', `❌ Erro na análise: ${err.message}\n\nTente novamente ou use um vídeo menor.`, null, false);
      setAnalysisState('idle');
      return;
    }

    if (result) {
      setApiData(normalizeSession(result));
      setMetrics(result.biomechanics);  // REAL data — not mock
      if (videoRef.current) {
        setVideoSize({ w: videoRef.current.clientWidth, h: videoRef.current.clientHeight });
      }
      setAnalysisState('done');

      // Navigate to the complete Race Result Page
      const sid = result.session_id || pendingSessionId || scanSessionId;
      if (sid) {
        navigate(`/swim-vision/result/${sid}`);
        return; // skip legacy tabs rendering
      }
    } else {
      setAnalysisState('idle');
      return;
    }

    // Use user-selected video mode
    const isComp = videoMode === 'competition';
    const cs = result?.competition_summary || (isComp ? _buildCompSummaryFromBio(result) : null);
    const bio = result?.biomechanics;
    const stroke = isComp ? null : bio?.stroke;
    const turns = isComp ? null : bio?.turn_detection;
    const splits = isComp ? null : bio?.split_analysis;
    const pipe = result?.pipeline;

    if (isComp && cs) {
      // Competition mode summary (quantitative only)
      const turnInfo = cs.turns?.length > 0 ? `\n🔄 ${cs.turns.length} virada(s) detectada(s)` : '';
      const splitInfo = cs.splits?.length > 0 ? `\n⏱️ Parciais: ${cs.splits.map(s => `${s.label}: ${s.time_s?.toFixed(2)}s`).join(' · ')}` : '';
      const warnInfo = cs.warnings?.length > 0 ? `\n⚠️ ${cs.warnings.join(' | ')}` : '';
      pushMsg(
        'assistant',
        `✅ Análise de **competição** completa!\n\n🏊 **${apiData?.canonicalStrokeCount || cs.stroke_count_total} braçadas** · Freq: ${cs.stroke_rate_avg?.toFixed(1)}/min\n📏 DPS: ${cs.dps_m?.toFixed(2)}m · Braç/m: ${cs.strokes_per_meter?.toFixed(2)}${turnInfo}${splitInfo}${warnInfo}\n\n🤖 ${pipe?.model_version || 'swimxyz'} · SC v${pipe?.stroke_counter_version || '?'} · ${pipe?.device || 'cpu'} · ${pipe?.processing_time_s?.toFixed(1) || '?'}s\n\nAbas: **Competição** (métricas), **Parciais**, **Eventos**, **Debug**.`,
        'alta',
        true
      );
    } else {
      // Training mode summary (full biomechanics)
      const strokeCount = apiData?.canonicalStrokeCount || stroke?.count || 0;
      const score = bio?.overall_score?.toFixed(0) || '—';
      const fatigue = stroke?.fatigue_index?.toFixed(3) || '—';
      const symmetry = ((stroke?.left_right_ratio || 0) * 100).toFixed(0);
      const turnInfo = turns?.turn_count > 0 ? `\n🔄 ${turns.turn_count} viradas detectadas · Score médio: ${turns.avg_quality_score?.toFixed(0)}/100` : '';
      const splitInfo = splits?.splits?.length > 0 ? `\n⏱️ Parciais: ${splits.splits.map(s => `${s.distance_label}: ${s.split_time_s?.toFixed(2)}s`).join(' · ')} · Pacing: ${splits.pacing_type}` : '';

      pushMsg(
        'assistant',
        `✅ Análise completa! Dados **reais** do SwimXYZ.\n\n🏊 **Score Geral: ${score}/100**\n📊 ${strokeCount} braçadas · Simetria L/R: ${symmetry}% · Fadiga: ${fatigue}\n⚡ Freq: ${stroke?.rate_per_min?.toFixed(1) || '—'}/min · Ciclo: ${stroke?.cycle_time_avg?.toFixed(2) || '—'}s${turnInfo}${splitInfo}\n\n🤖 Modelo: ${pipe?.model_version || 'swimxyz'} · SC v${pipe?.stroke_counter_version || '?'} · ${pipe?.device || 'cpu'} · ${pipe?.processing_time_s?.toFixed(1) || '?'}s\n\nExplore as abas "Métricas", "Ângulos" e "Parciais" no dashboard.`,
        'alta',
        true
      );
    }
  };

  // ── Resume Session (after timeout) ────────────────────────────
  const resumeSession = async (sid) => {
    const sessionId = sid || pendingSessionId || scanSessionId;
    if (!sessionId) return;

    pushMsg('assistant', `🔄 Verificando status da sessão #${sessionId}...`, null, false);
    setAnalysisState('analyzing');

    try {
      const session = await checkSessionStatus(sessionId);

      if (!session) {
        pushMsg('assistant', `❌ Sessão #${sessionId} não encontrada.`, null, false);
        setAnalysisState('idle');
        return;
      }

      if (session.status === 'completed') {
        // Session completed while we were away — extract result
        let result = null;
        if (session.full_report_json) {
          try {
            const report = typeof session.full_report_json === 'string'
              ? JSON.parse(session.full_report_json) : session.full_report_json;
            result = {
              session_id: sessionId,
              biomechanics: report.biomechanics || report,
              race_analysis: report.race_analysis || null,
              sanity: report.sanity || null,
              annotatedVideoUrl: session.annotatedVideoUrl || null,
              pipeline: {
                processing_time_s: (report.processing_time_ms || 0) / 1000,
                model_version: session.model_version || 'swimxyz',
                stroke_counter_version: session.stroke_counter_version || report?.stroke_counting?.stroke_counter_version || '—',
                device: session.device_used || 'cpu',
                data_source: 'real',
              },
            };
          } catch (e) { console.warn('Parse error:', e); }
        }
        if (result) {
          setApiData(normalizeSession(result));
          setMetrics(result.biomechanics);
        }
        setPendingSessionId(null);
        setAnalysisState('done');
        pushMsg('assistant', `✅ Análise completa! Score: ${result?.biomechanics?.overall_score?.toFixed(1) || '?'}/100`, null, false);
        // Navigate to the complete Race Result Page
        navigate(`/swim-vision/result/${sessionId}`);
      } else if (session.status === 'failed') {
        pushMsg('assistant', `❌ Análise falhou: ${session.error_message || 'Erro desconhecido'}`, null, false);
        setPendingSessionId(null);
        setAnalysisState('idle');
      } else {
        // Still processing — offer to poll again
        pushMsg('assistant', `⏳ Sessão #${sessionId} ainda está ${session.status === 'processing' ? 'processando' : 'na fila'}. Tente verificar novamente em 1-2 minutos.`, null, false);
        setAnalysisState('timeout');
      }
    } catch (err) {
      console.error('[SwimVision] Resume check failed:', err);
      pushMsg('assistant', `⚠️ Não foi possível verificar: ${err.message}. Tente novamente.`, null, false);
      setAnalysisState('timeout');
    }
  };

  // ── Chat ────────────────────────────────────────────────────────
  const pushMsg = (role, text, confidence = null, showFeedback = false) => {
    setMessages((prev) => [...prev, { role, text, confidence, showFeedback }]);
  };

  const sendMessage = () => {
    const txt = inputMsg.trim();
    if (!txt) return;
    setInputMsg('');
    pushMsg('user', txt);

    setTimeout(() => {
      let response = '';
      let confidence = 'média';
      const lower = txt.toLowerCase();

      // Use REAL pipeline data for chat responses
      const bio = metrics;
      const stroke = bio?.stroke;
      const turns = bio?.turn_detection;
      const splits = bio?.split_analysis;
      const scoreVal = bio?.overall_score?.toFixed(0) || '—';
      const canonicalCount = apiData?.canonicalStrokeCount || stroke?.count || 0;

      if (lower.includes('nado') || lower.includes('estilo') || lower.includes('classifica')) {
        response = `🏊 **Análise do Nado**\n\nO modelo SwimXYZ analisou a cinemática:\n• ${canonicalCount} braçadas detectadas\n• Freq: ${stroke?.rate_per_min?.toFixed(1) || '—'}/min\n• Simetria L/R: ${((stroke?.left_right_ratio || 0) * 100).toFixed(0)}%\n• Score geral: ${scoreVal}/100\n\n💡 Classificação automática de estilo em desenvolvimento.`;
        confidence = 'alta';
      } else if (lower.includes('angulo') || lower.includes('ângulo')) {
        const angleEntries = bio?.angles ? Object.entries(bio.angles).slice(0, 5) : [];
        const angleText = angleEntries.map(([name, a]) => `• ${name.replace(/_/g, ' ')}: ${a.mean?.toFixed(1)}° ± ${a.std?.toFixed(1)}° (${(a.in_range_pct || 0).toFixed(0)}% no range)`).join('\n');
        response = `📐 **Ângulos Corporais (dados reais)**\n\n${angleText || 'Sem dados de ângulos — execute a análise.'}\n\n💡 Consulte a aba "Ângulos" para detalhes completos.`;
        confidence = 'alta';
      } else if (lower.includes('virada') || lower.includes('turn')) {
        if (turns?.turn_count > 0) {
          const turnText = turns.turns.map(t => `• Virada ${t.turn_number}: ${t.wall_time_s?.toFixed(2)}s (${t.turn_type}) — score ${t.quality_score?.toFixed(0)}/100`).join('\n');
          response = `🔄 **Análise das Viradas (dados reais)**\n\n${turns.turn_count} viradas detectadas · Score médio: ${turns.avg_quality_score?.toFixed(0)}/100\n\n${turnText}\n\n💡 Viradas com score <70 podem melhorar com treino de impulsão e streamline.`;
        } else {
          response = `🔄 Prova de 50m — sem viradas detectadas. Para análise de viradas, use vídeos de provas 100m+.`;
        }
        confidence = 'alta';
      } else if (lower.includes('braçada') || lower.includes('catch') || lower.includes('stroke')) {
        response = `🏊 **Biomecânica da Braçada (dados reais)**\n\n📊 ${canonicalCount} braçadas totais (${stroke?.left_count || 0} esq. + ${stroke?.right_count || 0} dir.)\n⚡ Frequência: ${stroke?.rate_per_min?.toFixed(1) || '—'}/min\n⏱️ Ciclo médio: ${stroke?.cycle_time_avg?.toFixed(2) || '—'}s\n📉 Índice de fadiga: ${stroke?.fatigue_index?.toFixed(3) || '—'}\n\n${bio?.scores?.map(s => `• ${s.category.replace(/_/g, ' ')}: ${s.score?.toFixed(0)}/100 (${s.grade}) — ${s.feedback}`).join('\n') || ''}\n\n🎯 Score geral: ${scoreVal}/100`;
        confidence = 'alta';
      } else if (lower.includes('parcial') || lower.includes('split') || lower.includes('parciais')) {
        if (splits?.splits?.length > 0) {
          const splitText = splits.splits.map(s => `• ${s.distance_label}: ${s.split_time_s?.toFixed(2)}s (pace ${s.pace_per_100m_s?.toFixed(2)}s/100m)${s.is_fastest ? ' 🏆' : ''}${s.is_slowest ? ' ⚠️' : ''}`).join('\n');
          response = `⏱️ **Parciais (dados reais)**\n\nPacing: ${splits.pacing_type}\nTotal: ${splits.total_time_s?.toFixed(2)}s\n\n${splitText}\n\n1ª metade: ${splits.first_half_avg_s?.toFixed(2)}s\n2ª metade: ${splits.second_half_avg_s?.toFixed(2)}s\nΔ: ${splits.half_delta_s > 0 ? '+' : ''}${splits.half_delta_s?.toFixed(2)}s`;
        } else {
          response = `⏱️ Prova sem parciais — disponível para distâncias 100m+.`;
        }
        confidence = 'alta';
      } else if (lower.includes('melhora') || lower.includes('plano') || lower.includes('treino')) {
        const recs = bio?.recommendations || [];
        const recText = recs.map(r => `• **${r.priority}** — ${r.area}: ${r.suggestion}`).join('\n');
        response = `📋 **Recomendações (baseadas nos dados reais)**\n\n${recText || 'Execute a análise para gerar recomendações personalizadas.'}\n\n🎯 Score atual: ${scoreVal}/100`;
        confidence = 'alta';
      } else {
        response = `Analisei o vídeo com o modelo SwimXYZ (dados reais).\n\nScore: ${scoreVal}/100 · ${canonicalCount} braçadas\n\nSobre "${txt}" — posso ajudar com:\n• Detalhes da braçada e biomecânica\n• Análise de ângulos corporais\n• Viradas e parciais\n• Recomendações de melhoria\n\nUse as ações rápidas abaixo.`;
        confidence = 'baixa';
      }

      pushMsg('assistant', response, confidence, true);
    }, 600);
  };

  const handleQuickPrompt = (prompt) => {
    setInputMsg('');
    pushMsg('user', prompt);

    // Use REAL pipeline data for quick prompt responses
    const bio = metrics;
    const stroke = bio?.stroke;
    const turns = bio?.turn_detection;
    const splits = bio?.split_analysis;
    const scoreVal = bio?.overall_score?.toFixed(0) || '—';
    const canonicalCount = apiData?.canonicalStrokeCount || stroke?.count || 0;

    setTimeout(() => {
      let response = '';
      if (prompt.includes('técnica geral')) {
        const topScores = (bio?.scores || []).slice(0, 3).map(s => `• ${s.category.replace(/_/g, ' ')}: ${s.score?.toFixed(0)}/100 (${s.grade})`).join('\n');
        response = `🎯 **Análise Técnica Geral (dados reais do SwimXYZ)**\n\n📊 Score: ${scoreVal}/100\n🏊 ${canonicalCount} braçadas · Freq: ${stroke?.rate_per_min?.toFixed(1) || '—'}/min\n⚖️ Simetria L/R: ${((stroke?.left_right_ratio || 0) * 100).toFixed(0)}%\n📉 Fadiga: ${stroke?.fatigue_index?.toFixed(3) || '—'}\n\n**Scores por categoria:**\n${topScores || 'Execute a análise para ver scores.'}\n\n${turns?.turn_count > 0 ? `🔄 ${turns.turn_count} viradas — score médio ${turns.avg_quality_score?.toFixed(0)}/100` : ''}\n\nQuer que eu detalhe algum aspecto?`;
      } else if (prompt.includes('virada')) {
        if (turns?.turn_count > 0) {
          const best = turns.turns.reduce((a, b) => (a.quality_score > b.quality_score ? a : b), turns.turns[0]);
          const worst = turns.turns.reduce((a, b) => (a.quality_score < b.quality_score ? a : b), turns.turns[0]);
          response = `🔄 **Avaliação de Viradas (${turns.turn_count} detectadas — dados reais)**\n\n📍 Score médio: ${turns.avg_quality_score?.toFixed(0)}/100\n\n🏆 Melhor: Virada #${best.turn_number} (${best.wall_time_s?.toFixed(2)}s, ${best.turn_type}, score ${best.quality_score?.toFixed(0)})\n⚠️ Pior: Virada #${worst.turn_number} (${worst.wall_time_s?.toFixed(2)}s, ${worst.turn_type}, score ${worst.quality_score?.toFixed(0)})\n\n${turns.turns.map(t => `• Virada ${t.turn_number}: ${t.wall_time_s?.toFixed(2)}s · ${t.turn_type} · ${t.quality_score?.toFixed(0)}/100`).join('\n')}\n\n💡 Foque nas viradas com score <70.`;
        } else {
          response = `🔄 Prova curta — sem viradas detectadas. Parciais disponíveis para distâncias 100m+.`;
        }
      } else if (prompt.includes('braçada') || prompt.includes('Biomecânica')) {
        const angleEntries = bio?.angles ? Object.entries(bio.angles).slice(0, 4) : [];
        const angleText = angleEntries.map(([n, a]) => `• ${n.replace(/_/g, ' ')}: ${a.mean?.toFixed(1)}° (${(a.in_range_pct || 0).toFixed(0)}% no range)`).join('\n');
        response = `🏊 **Relatório Biomecânico (dados reais)**\n\n**Braçada:**\n• ${canonicalCount} braçadas (${stroke?.left_count || 0}E + ${stroke?.right_count || 0}D)\n• Freq: ${stroke?.rate_per_min?.toFixed(1) || '—'}/min · Ciclo: ${stroke?.cycle_time_avg?.toFixed(2) || '—'}s\n• Fadiga: ${stroke?.fatigue_index?.toFixed(3) || '—'}\n\n**Ângulos principais:**\n${angleText || 'Sem dados de ângulos.'}\n\n📊 Score: ${scoreVal}/100`;
      } else {
        const recs = bio?.recommendations || [];
        const recText = recs.slice(0, 3).map(r => `• **${r.area}**: ${r.suggestion}`).join('\n');
        response = `📋 **Recomendações (dados reais)**\n\n${recText || 'Execute a análise para recomendações.'}\n\n🎯 Score atual: ${scoreVal}/100\n⏱️ ${splits?.splits?.length > 0 ? `Pacing: ${splits.pacing_type} · Total: ${splits.total_time_s?.toFixed(2)}s` : 'Sem parciais.'}`;
      }
      pushMsg('assistant', response, 'alta', true);
    }, 700);
  };

  // ── Render ──────────────────────────────────────────────────────
  const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: VUKZ.bg }}>
      {/* ── Header ───────────────────────────────────────────── */}
      <div className="px-4 lg:px-6 pt-6 pb-0">
        <PageHeader
          title="Swim Vision"
          subtitle="Análise de movimentos com IA · Biomecânica · Digital Twin 3D"
          badgeText="COMPUTER VISION"
          badgeIcon={Eye}
          rightSlot={
            <div className="flex items-center gap-2">
              <Link
                to="/swim-vision/dashboard"
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-xs transition-all hover:shadow-md border"
                style={{ borderColor: VUKZ.border, color: VUKZ.text, backgroundColor: VUKZ.accent + '08' }}
              >
                <BarChart3 size={15} />
                Dashboard
              </Link>
              <button
                onClick={() => setDtModalOpen(true)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm transition-all hover:scale-105 shadow-lg"
                style={{
                  background: 'linear-gradient(135deg, #374151 0%, #1f2937 50%, #111827 100%)',
                  color: 'white',
                }}
              >
                <Box size={18} />
                DT Digital Twin
                <Sparkles size={14} className="opacity-70" />
              </button>
            </div>
          }
        />
      </div>

      {/* ── Main Layout ──────────────────────────────────────── */}
      <div className="flex-1 flex flex-col lg:flex-row gap-0 px-4 lg:px-6 pb-6 pt-4">

        {/* ═════ LEFT: Video Player ═════ */}
        <div className={`${expandedVideo ? 'w-full' : 'lg:w-[55%]'} flex flex-col gap-4 transition-all duration-300`}>
          {/* Video area */}
          <div className="bg-white rounded-2xl border overflow-hidden flex-1 flex flex-col" style={{ borderColor: VUKZ.border }}>
            {!videoUrl ? (
              /* Upload Zone */
              <label className="flex-1 flex items-center justify-center cursor-pointer group">
                <input type="file" accept="video/*,.mts,.m2ts,.mkv" className="hidden" onChange={handleFileSelect} />
                {converting ? (
                  <div className="text-center p-12">
                    <div className="w-24 h-24 mx-auto mb-5 rounded-2xl flex items-center justify-center animate-pulse" style={{ background: VUKZ_GRADIENT }}>
                      <Video size={36} color="white" />
                    </div>
                    <p className="text-lg font-black mb-2" style={{ color: VUKZ.text }}>Convertendo vídeo...</p>
                    <p className="text-sm mb-4" style={{ color: VUKZ.textLight }}>{convertStatus}</p>
                    <div className="w-64 mx-auto h-2 rounded-full overflow-hidden" style={{ backgroundColor: VUKZ.border }}>
                      <div className="h-full rounded-full transition-all duration-300" style={{ width: `${convertProgress}%`, background: VUKZ_GRADIENT }} />
                    </div>
                    <p className="text-xs mt-2 font-mono" style={{ color: VUKZ.textLight }}>{Math.round(convertProgress)}%</p>
                  </div>
                ) : (
                <div className="text-center p-12">
                  <div
                    className="w-24 h-24 mx-auto mb-5 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform"
                    style={{ background: VUKZ_GRADIENT }}
                  >
                    <Upload size={36} color="white" />
                  </div>
                  <p className="text-lg font-black mb-1" style={{ color: VUKZ.text }}>Upload de Vídeo</p>
                  <p className="text-sm mb-4" style={{ color: VUKZ.textLight }}>
                    Arraste ou clique para selecionar
                  </p>
                  <div className="flex items-center justify-center gap-3">
                    {['MP4', 'MOV', 'AVI', 'MTS', 'MKV'].map((f) => (
                      <span key={f} className="text-[10px] font-bold px-2.5 py-1 rounded-full" style={{ backgroundColor: VUKZ.primaryLight, color: VUKZ.textLight }}>
                        {f}
                      </span>
                    ))}
                    <span className="text-[10px]" style={{ color: VUKZ.textLight }}>· Máx 500MB</span>
                  </div>
                  <Link
                    to="/swim-vision/batch"
                    className="inline-flex items-center gap-1.5 mt-4 text-xs font-bold px-4 py-2 rounded-lg border transition-all hover:bg-gray-50"
                    style={{ borderColor: VUKZ.border, color: VUKZ.textLight }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Layers size={13} /> Análise em Lote (até 8 vídeos)
                  </Link>
                </div>
                )}
              </label>
            ) : (
              <>
                {/* Player */}
                <div ref={videoContainerRef} className="flex-1 bg-black relative group">
                  {/* Expand/collapse toggle */}
                  <button
                    onClick={() => setExpandedVideo(!expandedVideo)}
                    className="absolute top-3 right-3 z-20 p-2 rounded-lg backdrop-blur-md transition-all hover:scale-110"
                    style={{ backgroundColor: 'rgba(0,0,0,0.5)', color: 'white' }}
                    title={expandedVideo ? 'Reduzir vídeo' : 'Expandir vídeo'}
                  >
                    {expandedVideo ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
                  </button>
                  <video
                    ref={videoRef}
                    src={activeVideoSrc}
                    className="w-full h-full object-contain"
                    style={{ maxHeight: expandedVideo ? 'calc(100vh - 200px)' : 'calc(100vh - 300px)', minHeight: expandedVideo ? '500px' : '380px' }}
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={(e) => {
                      handleLoadedMetadata();
                      setVideoSize({ w: e.target.clientWidth, h: e.target.clientHeight });
                    }}
                    onClick={togglePlay}
                    onError={(e) => {
                      const msg = e.target.error?.message || 'Formato não suportado';
                      console.error('[Video] Load error:', msg, 'src:', activeVideoSrc?.substring(0, 80));
                      // If annotated video fails, fall back to original video
                      if (showAnnotatedVideo && annotatedVideoUrl && videoUrl) {
                        console.warn('[Video] Annotated video failed, falling back to original');
                        setShowAnnotatedVideo(false);
                      }
                    }}
                  />

                  {/* Skeleton Overlay — disabled in direct mode (real pipeline returns aggregated data, not per-frame) */}

                  {/* Annotated video indicator (when showing overlay) */}
                  {showAnnotatedVideo && annotatedVideoUrl && (
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 px-3 py-1 rounded-full backdrop-blur-md text-[10px] font-black" style={{ backgroundColor: 'rgba(245,158,11,0.85)', color: 'white' }}>
                      🎯 Vídeo Anotado — Overlay IA
                    </div>
                  )}

                  {/* Real pipeline badges — Score + Model + Processing time */}
                  {analysisState === 'done' && apiData && (
                    <div className="absolute top-3 left-3 z-20 flex items-center gap-2 px-3 py-2 rounded-xl backdrop-blur-md shadow-lg" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center text-lg" style={{ backgroundColor: '#6366f125' }}>
                        🏊
                      </div>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-black text-white">Score: {metrics?.overall_score?.toFixed(0) || '—'}/100</span>
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: metrics?.overall_score >= 75 ? '#10b98130' : '#f59e0b30', color: metrics?.overall_score >= 75 ? '#10b981' : '#f59e0b' }}>
                            {apiData?.canonicalStrokeCount || metrics?.stroke?.count || 0} braçadas
                          </span>
                        </div>
                        <span className="text-[9px] text-gray-400">SwimXYZ · Dados reais da IA</span>
                      </div>
                    </div>
                  )}

                  {/* Turn count badge (top-right) */}
                  {analysisState === 'done' && metrics?.turn_detection?.turn_count > 0 && (
                    <div className="absolute top-3 right-3 z-20 flex items-center gap-2 px-3 py-2 rounded-xl backdrop-blur-md shadow-lg" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center text-base" style={{ backgroundColor: '#8b5cf625' }}>
                        🔄
                      </div>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-black text-white">{metrics.turn_detection.turn_count} Viradas</span>
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#8b5cf630', color: '#8b5cf6' }}>
                            {metrics.turn_detection.avg_quality_score?.toFixed(0)}/100
                          </span>
                        </div>
                        <span className="text-[9px] text-gray-400">Qualidade média das viradas</span>
                      </div>
                    </div>
                  )}

                  {/* Video mode badge (top-left, below stroke badge) */}
                  {analysisState === 'done' && (
                    <div className="absolute top-16 left-3 z-20 px-2 py-1 rounded-lg backdrop-blur-md text-[10px] font-bold" style={{ backgroundColor: 'rgba(0,0,0,0.6)', color: videoMode === 'competition' ? '#f59e0b' : '#60a5fa' }}>
                      {videoMode === 'competition' ? '🏆 Competição' : '🏋️ Treino'}
                    </div>
                  )}

                  {/* Original / Annotated toggle (bottom-center) — only when annotated video exists */}
                  {analysisState === 'done' && annotatedVideoUrl && (
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-0.5 rounded-lg backdrop-blur-md shadow-lg overflow-hidden" style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}>
                      <button
                        onClick={() => setShowAnnotatedVideo(false)}
                        className="px-3 py-1.5 text-[10px] font-bold transition-all"
                        style={{
                          backgroundColor: !showAnnotatedVideo ? '#6366f1' : 'transparent',
                          color: !showAnnotatedVideo ? 'white' : '#9ca3af',
                        }}
                      >
                        Original
                      </button>
                      <button
                        onClick={() => setShowAnnotatedVideo(true)}
                        className="px-3 py-1.5 text-[10px] font-bold transition-all"
                        style={{
                          backgroundColor: showAnnotatedVideo ? '#f59e0b' : 'transparent',
                          color: showAnnotatedVideo ? 'white' : '#9ca3af',
                        }}
                      >
                        🎯 Annotated
                      </button>
                    </div>
                  )}

                  {/* Model + processing time badge (bottom-left) */}
                  {analysisState === 'done' && apiData && (
                    <div className="absolute bottom-3 left-3 z-20 flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[9px] font-bold backdrop-blur-sm" style={{ backgroundColor: 'rgba(0,0,0,0.6)', color: '#a78bfa' }}>
                      <Sparkles size={10} />
                      {apiData.pipeline?.model_version || 'swimxyz'}
                      <span style={{ color: '#6b7280' }}>· {apiData.pipeline?.processing_time_s?.toFixed(1)}s</span>
                      <span style={{ color: '#10b981' }}>· REAL</span>
                    </div>
                  )}

                  {/* Data source badge (bottom-right) */}
                  {analysisState === 'done' && apiData?.pipeline && (
                    <div className="absolute bottom-3 right-3 z-20 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg backdrop-blur-sm" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
                      <Cpu size={11} style={{ color: '#60a5fa' }} />
                      <span className="text-[10px] font-bold" style={{ color: '#60a5fa' }}>
                        {apiData.pipeline.device?.toUpperCase()}
                      </span>
                      <span className="text-[9px] font-semibold" style={{ color: '#9ca3af' }}>
                        · {apiData.pipeline.max_frames} frames
                      </span>
                    </div>
                  )}

                  {/* Overlay play button */}
                  {!isPlaying && (
                    <button onClick={togglePlay} className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center">
                        <Play size={28} style={{ color: VUKZ.primary }} />
                      </div>
                    </button>
                  )}

                  {/* Analysis overlay */}
                  {analysisState === 'analyzing' && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <div className="bg-white rounded-2xl p-6 w-80 shadow-2xl">
                        <p className="text-sm font-black mb-4 text-center" style={{ color: VUKZ.text }}>
                          {analysisStage === 0 ? 'Enviando vídeo...' : 'Analisando vídeo...'}
                        </p>

                        {/* Upload progress bar (stage 0) */}
                        {analysisStage === 0 && uploadProgress > 0 && (
                          <div className="mb-4">
                            <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: VUKZ.primaryLight }}>
                              <div
                                className="h-full rounded-full transition-all duration-300"
                                style={{ width: `${uploadProgress}%`, background: VUKZ.primary }}
                              />
                            </div>
                            <p className="text-[10px] text-center mt-1 font-bold" style={{ color: VUKZ.textLight }}>
                              {uploadProgress}%
                            </p>
                          </div>
                        )}

                        <div className="space-y-2">
                          {ANALYSIS_STAGES.map((stage, i) => {
                            const StageIcon = stage.icon;
                            const isDone = i < analysisStage;
                            const isCurrent = i === analysisStage;
                            return (
                              <div key={stage.key} className="flex items-center gap-3">
                                <div
                                  className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${isCurrent ? 'animate-pulse' : ''}`}
                                  style={{
                                    backgroundColor: isDone ? `${VUKZ.success}15` : isCurrent ? `${VUKZ.primary}10` : VUKZ.primaryLight,
                                  }}
                                >
                                  {isDone ? (
                                    <CheckCircle2 size={14} style={{ color: VUKZ.success }} />
                                  ) : (
                                    <StageIcon size={14} style={{ color: isCurrent ? VUKZ.primary : VUKZ.textLight }} />
                                  )}
                                </div>
                                <span
                                  className="text-xs font-semibold"
                                  style={{ color: isDone ? VUKZ.success : isCurrent ? VUKZ.text : VUKZ.textLight }}
                                >
                                  {stage.label}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Timeout overlay — analysis still processing on server */}
                  {analysisState === 'timeout' && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <div className="bg-white rounded-2xl p-6 w-80 shadow-2xl text-center">
                        <div className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center" style={{ backgroundColor: '#fef3c7' }}>
                          <Timer size={24} style={{ color: '#d97706' }} />
                        </div>
                        <p className="text-sm font-black mb-2" style={{ color: VUKZ.text }}>Análise em andamento</p>
                        <p className="text-xs mb-4" style={{ color: VUKZ.textLight }}>
                          {pendingSessionId
                            ? `Sessão #${pendingSessionId} ainda está processando no servidor. Isso pode levar alguns minutos para vídeos longos.`
                            : 'A análise continua processando no servidor.'}
                        </p>
                        <div className="flex flex-col gap-2">
                          <button
                            onClick={() => resumeSession()}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all hover:scale-105"
                            style={{ background: VUKZ_GRADIENT, color: 'white' }}
                          >
                            <RefreshCw size={14} /> Verificar Status
                          </button>
                          <button
                            onClick={() => { setAnalysisState('idle'); setPendingSessionId(null); }}
                            className="w-full px-4 py-2 rounded-xl text-xs font-semibold transition-all"
                            style={{ color: VUKZ.textLight, border: `1px solid ${VUKZ.border}` }}
                          >
                            Voltar
                          </button>
                        </div>
                      </div>
                    </div>
                  )}}
                </div>

                {/* Controls bar */}
                <div className="p-3 border-t" style={{ borderColor: VUKZ.border }}>
                  {/* Timeline */}
                  <div className="flex items-center gap-3 mb-3">
                    <span ref={timeDisplayRef} className="text-[10px] font-mono font-bold w-16 text-right" style={{ color: VUKZ.textLight }}>
                      {formatTime(currentTime)}
                    </span>
                    <div
                      className="flex-1 h-1.5 rounded-full cursor-pointer relative group/bar"
                      style={{ backgroundColor: VUKZ.primaryLight }}
                      onClick={seekTo}
                    >
                      <div ref={progressBarRef} className="h-full rounded-full" style={{ width: `${progressPct}%`, backgroundColor: VUKZ.primary }} />
                      <div
                        ref={progressDotRef}
                        className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white border-2 shadow opacity-0 group-hover/bar:opacity-100 transition-opacity"
                        style={{ left: `${progressPct}%`, borderColor: VUKZ.primary, transform: `translate(-50%, -50%)` }}
                      />
                    </div>
                    <span className="text-[10px] font-mono font-bold w-16" style={{ color: VUKZ.textLight }}>
                      {formatTime(duration)}
                    </span>
                  </div>

                  {/* Row 1: Playback controls + Speed */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => skipFrames(-1)} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-all">
                        <SkipBack size={14} style={{ color: VUKZ.text }} />
                      </button>
                      <button onClick={togglePlay} className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: VUKZ_GRADIENT }}>
                        {isPlaying ? <Pause size={16} color="white" /> : <Play size={16} color="white" />}
                      </button>
                      <button onClick={() => skipFrames(1)} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-all">
                        <SkipForward size={14} style={{ color: VUKZ.text }} />
                      </button>
                    </div>

                    {/* Speed buttons */}
                    <div className="flex items-center gap-1">
                      {[0.25, 0.5, 1, 1.5, 2].map((s) => (
                        <button
                          key={s}
                          onClick={() => changeSpeed(s)}
                          className="px-2 py-1 rounded-md text-[10px] font-bold transition-all"
                          style={{
                            backgroundColor: playbackSpeed === s ? VUKZ.primary : 'transparent',
                            color: playbackSpeed === s ? 'white' : VUKZ.textLight,
                          }}
                        >
                          {s}x
                        </button>
                      ))}
                    </div>

                    {/* Model Badge */}
                    {bestModel && (
                      <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg" style={{ backgroundColor: `${VUKZ.primary}08`, border: `1px solid ${VUKZ.primary}20` }}>
                        <Shield size={10} style={{ color: VUKZ.primary }} />
                        <span className="text-[9px] font-bold" style={{ color: VUKZ.primary }}>
                          {bestModel.display_name || 'SwimXYZ'} · PCK {(bestModel.metrics?.pck_at_05 || 0.902).toFixed(3)}
                        </span>
                        {medleyReady?.ready && (
                          <span className="text-[8px] px-1 py-0.5 rounded-full font-bold" style={{ backgroundColor: `${VUKZ.success}15`, color: VUKZ.success }}>
                            Medley ✓
                          </span>
                        )}
                      </div>
                    )}

                    {analysisState === 'done' && (
                      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg" style={{ backgroundColor: `${VUKZ.success}10` }}>
                        <CheckCircle2 size={12} style={{ color: VUKZ.success }} />
                        <span className="text-[10px] font-bold" style={{ color: VUKZ.success }}>Análise concluída</span>
                      </div>
                    )}
                  </div>

                  {/* Row 2: Style + Distance + Lane selectors + Analyze button */}
                  {analysisState === 'idle' && videoUrl && (
                    <div className="mt-1.5 pt-1.5 space-y-2" style={{ borderTop: `1px solid ${VUKZ.border}` }}>
                      {/* Video Mode toggle */}
                      <div className="flex items-center gap-3 pb-3 mb-3" style={{ borderBottom: `1px solid ${VUKZ.border}` }}>
                        <span className="text-xs font-bold whitespace-nowrap" style={{ color: VUKZ.textLight }}>Modo:</span>
                        <div className="flex gap-2">
                          {[
                            { value: 'competition', label: 'Competição', desc: 'Câmera aberta, foco quantitativo' },
                            { value: 'training',   label: 'Treino',      desc: 'Nadador solo, biomecânica completa' },
                          ].map(m => (
                            <button
                              key={m.value}
                              onClick={() => setVideoMode(m.value)}
                              className="px-4 py-2 rounded-lg text-xs font-bold transition-all"
                              style={{
                                backgroundColor: videoMode === m.value ? VUKZ.primary : `${VUKZ.border}`,
                                color: videoMode === m.value ? 'white' : VUKZ.textLight,
                                border: `2px solid ${videoMode === m.value ? VUKZ.primary : VUKZ.border}`,
                              }}
                              title={m.desc}
                            >
                              {m.label}
                            </button>
                          ))}
                        </div>
                        <span className="text-[10px] ml-1" style={{ color: VUKZ.textLight }}>
                          {videoMode === 'competition' ? 'Braçadas, freq, DPS, parciais, viradas' : 'Ângulos, scores, simetria, insights'}
                        </span>
                      </div>

                      {/* Vincular Prova (pre-analysis race link) */}
                      <div className="flex items-center gap-3 pb-3 mb-1" style={{ borderBottom: `1px solid ${VUKZ.border}` }}>
                        <span className="text-xs font-bold whitespace-nowrap" style={{ color: VUKZ.textLight }}>Prova:</span>
                        {raceLink ? (
                          <div className="flex items-center gap-2 flex-1">
                            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg flex-1" style={{ backgroundColor: '#f0fdf4', border: '1px solid #86efac' }}>
                              <Link2 size={12} style={{ color: '#16a34a' }} />
                              <span className="text-[11px] font-bold truncate" style={{ color: '#15803d' }}>
                                {raceLink.competition_name} — {raceLink.event_name} ({raceLink.phase})
                              </span>
                              {raceLink.lanes?.length > 0 && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold" style={{ backgroundColor: '#dcfce7', color: '#16a34a' }}>
                                  {raceLink.lanes.length} raias
                                </span>
                              )}
                            </div>
                            <button
                              onClick={() => setShowLinkRaceModal(true)}
                              className="px-3 py-1.5 rounded-lg text-[10px] font-bold transition-all"
                              style={{ backgroundColor: VUKZ.border, color: VUKZ.textLight }}
                            >
                              Alterar
                            </button>
                            <button
                              onClick={() => setRaceLink(null)}
                              className="p-1.5 rounded-lg hover:bg-red-50 transition-all"
                              title="Remover vínculo"
                            >
                              <X size={12} style={{ color: '#dc2626' }} />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setShowLinkRaceModal(true)}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all hover:shadow-sm"
                            style={{
                              backgroundColor: 'transparent',
                              color: VUKZ.primary,
                              border: `2px dashed ${VUKZ.primary}40`,
                            }}
                          >
                            <Link2 size={14} />
                            Vincular Prova
                          </button>
                        )}
                      </div>

                      {/* Style & Distance row */}
                      <div className="flex flex-wrap items-center gap-4">
                        {/* Swimming Style selector */}
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold whitespace-nowrap" style={{ color: VUKZ.textLight }}>Estilo:</span>
                          <div className="flex gap-1.5">
                            {[
                              { value: 'freestyle',    label: 'Livre' },
                              { value: 'backstroke',   label: 'Costas' },
                              { value: 'butterfly',    label: 'Borboleta' },
                              { value: 'breaststroke', label: 'Peito' },
                              { value: 'medley',       label: 'Medley' },
                            ].map(s => (
                              <button
                                key={s.value}
                                onClick={() => {
                                  setSwimStyle(s.value);
                                  if (s.value === 'medley' && raceDistance < 200) setRaceDistance(200);
                                }}
                                className="px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all"
                                style={{
                                  backgroundColor: swimStyle === s.value ? VUKZ.primary : `${VUKZ.border}`,
                                  color: swimStyle === s.value ? 'white' : VUKZ.textLight,
                                  border: `1px solid ${swimStyle === s.value ? VUKZ.primary : VUKZ.border}`
                                }}
                              >
                                {s.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Race Distance selector */}
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold whitespace-nowrap" style={{ color: VUKZ.textLight }}>Distância:</span>
                          <div className="flex gap-1.5">
                            {[50, 100, 200, 400, 800, 1500].map(d => (
                              <button
                                key={d}
                                onClick={() => setRaceDistance(d)}
                                className="px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all"
                                style={{
                                  backgroundColor: raceDistance === d ? VUKZ.primary : `${VUKZ.border}`,
                                  color: raceDistance === d ? 'white' : VUKZ.textLight,
                                  border: `1px solid ${raceDistance === d ? VUKZ.primary : VUKZ.border}`
                                }}
                              >
                                {d}m
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Lane selector + Mode toggle + Analyze button row */}
                      <div className="flex flex-col gap-3 mt-1">
                        {/* Scan toggle + Lane selector */}
                        <div className="flex items-center gap-3">
                          {/* Scan mode toggle */}
                          <button
                            onClick={() => { setScanMode(!scanMode); setLaneScanResult(null); setScanSessionId(null); }}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold transition-all whitespace-nowrap"
                            style={{
                              backgroundColor: scanMode ? '#f59e0b' : `${VUKZ.border}`,
                              color: scanMode ? 'white' : VUKZ.textLight,
                              border: `1px solid ${scanMode ? '#f59e0b' : VUKZ.border}`,
                            }}
                            title="Detectar automaticamente quais raias têm nadadores"
                          >
                            <Eye size={14} /> {scanMode ? 'Auto-Detect ON' : 'Auto-Detect'}
                          </button>

                          {/* Manual lane selector (only when NOT in scan mode) */}
                          {!scanMode && (
                            <>
                              <span className="text-xs font-bold whitespace-nowrap" style={{ color: VUKZ.textLight }}>Raias:</span>
                              <div className="grid grid-cols-8 gap-1.5">
                                {[1,2,3,4,5,6,7,8].map(lane => (
                                  <button
                                    key={lane}
                                    onClick={() => setSelectedLanes(prev => prev.includes(lane) ? prev.filter(l => l !== lane) : [...prev, lane])}
                                    className="w-9 h-9 rounded-lg text-xs font-bold transition-all flex items-center justify-center"
                                    style={{
                                      backgroundColor: selectedLanes.includes(lane) ? VUKZ.primary : `${VUKZ.border}`,
                                      color: selectedLanes.includes(lane) ? 'white' : VUKZ.textLight,
                                      border: `1px solid ${selectedLanes.includes(lane) ? VUKZ.primary : VUKZ.border}`
                                    }}
                                  >
                                    {lane}
                                  </button>
                                ))}
                              </div>
                              <span className="text-[10px] whitespace-nowrap" style={{ color: VUKZ.textLight }}>
                                {selectedLanes.length === 0 ? '(todas)' : selectedLanes.join(', ')}
                              </span>
                            </>
                          )}
                        </div>

                        {/* Trim + Analyze buttons */}
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => setShowTrimmer(!showTrimmer)}
                            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all hover:scale-105 whitespace-nowrap border-2"
                            style={{
                              borderColor: showTrimmer ? '#f59e0b' : VUKZ.border,
                              backgroundColor: showTrimmer ? '#fffbeb' : 'transparent',
                              color: showTrimmer ? '#d97706' : VUKZ.textLight,
                            }}
                            title="Cortar vídeo antes da análise — selecione apenas o trecho da prova"
                          >
                            <Scissors size={14} /> Cortar
                          </button>
                          <button
                            onClick={startAnalysis}
                            className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold transition-all hover:scale-105 whitespace-nowrap"
                            style={{ background: VUKZ_GRADIENT, color: 'white' }}
                          >
                            {scanMode
                              ? <><Eye size={16} /> Detectar Raias</>
                              : <><Brain size={16} /> Analisar com IA</>
                            }
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ── Video Trimmer (inline below controls) ── */}
                  {showTrimmer && analysisState === 'idle' && videoUrl && (
                    <VideoTrimmer
                      videoRef={videoRef}
                      videoUrl={videoUrl}
                      duration={duration}
                      isProcessing={clipProcessing}
                      onCancel={() => setShowTrimmer(false)}
                      onTrimConfirm={async (startMs, endMs) => {
                        setClipProcessing(true);
                        try {
                          pushMsg('assistant', `✂️ Cortando vídeo: ${(startMs/1000).toFixed(1)}s → ${(endMs/1000).toFixed(1)}s (${((endMs-startMs)/1000).toFixed(1)}s)...`, null, false);

                          // Step 1: Upload full video to blob only (no session)
                          pushMsg('assistant', '📤 Enviando vídeo para o servidor...', null, false);
                          setAnalysisState('analyzing');
                          setAnalysisStage(0);
                          setUploadProgress(0);

                          const sourceBlobUrl = await uploadVideoToBlob(videoFile, (e) => {
                            if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
                          });

                          pushMsg('assistant', '✅ Upload concluído! Gerando clip cortado com FFmpeg...', null, false);
                          setAnalysisStage(1);

                          // Step 2: Create clip on backend (FFmpeg trim + auto-analyze)
                          const clipResp = await createVideoClip({
                            source_blob_url: sourceBlobUrl,
                            clip_start_ms: startMs,
                            clip_end_ms: endMs,
                            style: swimStyle,
                            distance_m: raceDistance,
                            lane: selectedLanes?.[0] || null,
                            pool_length_m: 50,
                            purpose: 'race_only',
                            auto_analyze: true,
                          });

                          if (!clipResp?.success) throw new Error(clipResp?.error || 'Clip creation failed');

                          const clipId = clipResp.data.clip_id;
                          pushMsg('assistant', `✅ Clip #${clipId} criado! Aguardando corte + análise automática...`, null, false);
                          setAnalysisStage(2);

                          // Step 3: Poll clip until ready
                          const readyClip = await pollClipUntilReady(clipId, { interval: 3000, timeout: 180000 });

                          // Step 4: If auto_analyze created a session, redirect to status page
                          const sessionId = readyClip.analysis_session_id;
                          if (sessionId) {
                            pushMsg('assistant', `🚀 Análise iniciada na sessão #${sessionId}! Redirecionando...`, null, false);
                            setShowTrimmer(false);
                            navigate(`/swim-vision/session/${sessionId}/status`);
                          } else {
                            pushMsg('assistant', `✅ Clip pronto! URL: ${readyClip.clip_blob_url}`, null, false);
                            setAnalysisState('idle');
                          }

                        } catch (err) {
                          console.error('[VideoTrimmer] Error:', err);
                          pushMsg('assistant', `❌ Erro no corte: ${err.message}`, null, false);
                          setAnalysisState('idle');
                        } finally {
                          setClipProcessing(false);
                        }
                      }}
                    />
                  )}

                  {/* ── Lane Scan Results (after scan completes) ── */}
                  {analysisState === 'scanned' && laneScanResult && (
                    <div className="mt-1.5 pt-1.5 space-y-2" style={{ borderTop: `1px solid ${VUKZ.border}` }}>
                      <div className="flex items-center gap-2 mb-1">
                        <Eye size={14} style={{ color: '#f59e0b' }} />
                        <span className="text-[11px] font-black" style={{ color: VUKZ.text }}>
                          Raias Detectadas — {laneScanResult.n_occupied || 0} ocupadas
                        </span>
                        <span className="text-[9px]" style={{ color: VUKZ.textLight }}>
                          ({laneScanResult.scan_time_ms}ms)
                        </span>
                      </div>

                      {/* Lane grid with scan results */}
                      <div className="grid grid-cols-8 gap-2">
                        {(laneScanResult.lanes_detected || []).map(lane => {
                          const isSelected = selectedLanes.includes(lane.lane);
                          const bgColor = lane.status === 'occupied'
                            ? (isSelected ? VUKZ.primary : '#10b981')
                            : (lane.status === 'uncertain' ? '#f59e0b' : `${VUKZ.border}`);
                          const textColor = lane.status === 'empty' && !isSelected ? VUKZ.textLight : 'white';

                          return (
                            <button
                              key={lane.lane}
                              onClick={() => setSelectedLanes(prev =>
                                prev.includes(lane.lane) ? prev.filter(l => l !== lane.lane) : [...prev, lane.lane]
                              )}
                              className="flex flex-col items-center py-2.5 rounded-lg text-center transition-all"
                              style={{
                                backgroundColor: isSelected ? VUKZ.primary : bgColor,
                                border: `2px solid ${isSelected ? VUKZ.primary : 'transparent'}`,
                                opacity: lane.status === 'empty' && !isSelected ? 0.5 : 1,
                              }}
                            >
                              <span className="text-xs font-bold" style={{ color: textColor }}>{lane.lane}</span>
                              <span className="text-[10px] font-bold mt-0.5" style={{ color: textColor }}>
                                {Math.round(lane.confidence * 100)}%
                              </span>
                            </button>
                          );
                        })}
                      </div>

                      {/* Athlete identification per selected lane */}
                      {selectedLanes.length > 0 && (
                        <div className="space-y-1.5 pt-1">
                          <span className="text-[10px] font-bold" style={{ color: VUKZ.textLight }}>👤 Identificar atletas nas raias selecionadas:</span>
                          <div className="grid grid-cols-2 gap-1.5">
                            {selectedLanes.sort((a, b) => a - b).map(lane => (
                              <div key={lane} className="flex items-center gap-1.5">
                                <span className="text-[10px] font-bold w-10 text-center py-1 rounded" style={{ backgroundColor: VUKZ.primary, color: 'white' }}>R{lane}</span>
                                <input
                                  type="text"
                                  placeholder="Nome do atleta"
                                  value={laneAthletes[lane] || ''}
                                  onChange={(e) => setLaneAthletes(prev => ({ ...prev, [lane]: e.target.value }))}
                                  className="flex-1 px-2 py-1 rounded text-[11px]"
                                  style={{ border: `1px solid ${VUKZ.border}`, color: VUKZ.text }}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Style + Mode + Analyze button for post-scan */}
                      <div className="flex flex-col gap-3 pt-2">
                        <div className="flex items-center gap-4">
                          {/* Mode toggle */}
                          <div className="flex gap-1.5">
                            {[
                              { value: 'competition', label: 'Competição'},
                              { value: 'training',   label: 'Treino'},
                            ].map(m => (
                              <button key={m.value} onClick={() => setVideoMode(m.value)}
                                className="px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all"
                                style={{
                                  backgroundColor: videoMode === m.value ? VUKZ.primary : `${VUKZ.border}`,
                                  color: videoMode === m.value ? 'white' : VUKZ.textLight,
                                }}
                              >{m.label}</button>
                            ))}
                          </div>
                          {/* Style */}
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold" style={{ color: VUKZ.textLight }}>Estilo:</span>
                            <div className="flex gap-1.5">
                              {[
                                { value: 'freestyle',    label: 'Livre' },
                                { value: 'backstroke',   label: 'Costas' },
                                { value: 'butterfly',    label: 'Borboleta' },
                                { value: 'breaststroke', label: 'Peito' },
                                { value: 'medley',       label: 'Medley' },
                              ].map(s => (
                                <button
                                  key={s.value}
                                  onClick={() => setSwimStyle(s.value)}
                                  className="px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all"
                                  style={{
                                    backgroundColor: swimStyle === s.value ? VUKZ.primary : `${VUKZ.border}`,
                                    color: swimStyle === s.value ? 'white' : VUKZ.textLight,
                                  }}
                                >
                                  {s.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="flex justify-end">
                          <button
                            onClick={analyzeAfterScan}
                            disabled={selectedLanes.length === 0}
                            className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold transition-all hover:scale-105 whitespace-nowrap disabled:opacity-40 disabled:hover:scale-100"
                            style={{ background: VUKZ_GRADIENT, color: 'white' }}
                          >
                            <Brain size={16} /> Analisar {selectedLanes.length > 0 ? `Raia${selectedLanes.length > 1 ? 's' : ''} ${selectedLanes.join(', ')}` : ''}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* ═════ Dashboard Metrics (below video) ═════ */}
          {/* Timeline strip removed — real pipeline provides split data instead */}

          {metrics && (() => {
            const isCompMode = videoMode === 'competition';
            const allTabs = [
              { key: 'metrics', label: isCompMode ? 'Competição' : 'Métricas', icon: isCompMode ? Zap : BarChart3 },
              ...(!isCompMode ? [{ key: 'angles', label: 'Ângulos Corporais', icon: Activity }] : []),
              { key: 'splits', label: 'Parciais', icon: Clock },
              { key: 'events', label: 'Eventos', icon: Target },
              ...((apiData?.strokeChartUrl || apiData?.strokeTiming) ? [{ key: 'timing', label: 'Timing', icon: Timer }] : []),
              { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
              ...(!isCompMode ? [{ key: 'insights', label: 'Insights IA', icon: Brain }] : []),
            ];
            return (
            <div className="bg-white rounded-2xl border overflow-hidden" style={{ borderColor: VUKZ.border }}>
              {/* Tabs */}
              <div className="flex border-b overflow-x-auto" style={{ borderColor: VUKZ.border }}>
                {allTabs.map((tab) => {
                  const TabIcon = tab.icon;
                  const active = activeTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className="flex-1 flex items-center justify-center gap-2 py-3 text-xs font-bold transition-all relative"
                      style={{ color: active ? VUKZ.text : VUKZ.textLight }}
                    >
                      <TabIcon size={14} />
                      {tab.label}
                      {active && (
                        <div className="absolute bottom-0 left-1/4 right-1/4 h-0.5 rounded-full" style={{ backgroundColor: VUKZ.primary }} />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Tab content */}
              <div className="p-4">
                {activeTab === 'metrics' && (
                  <div>
                    {/* Pipeline info card */}
                    {apiData?.pipeline && (
                      <div className="flex flex-wrap gap-2 mb-4 p-3 rounded-xl" style={{ backgroundColor: '#f0fdf4' }}>
                        <span className="text-[10px] font-bold px-2 py-1 rounded-full" style={{ backgroundColor: '#10b98115', color: '#10b981' }}>
                          ✓ Dados reais
                        </span>
                        {isCompMode && (
                          <span className="text-[10px] font-bold px-2 py-1 rounded-full" style={{ backgroundColor: '#6366f115', color: '#6366f1' }}>
                            🏊 Modo Competição
                          </span>
                        )}
                        <span className="text-[10px] font-bold px-2 py-1 rounded-full" style={{ backgroundColor: '#6366f115', color: '#6366f1' }}>
                          {apiData.pipeline.model_version}
                        </span>
                        <span className="text-[10px] font-bold px-2 py-1 rounded-full" style={{ backgroundColor: '#f59e0b15', color: '#f59e0b' }}>
                          {apiData.pipeline.device?.toUpperCase()} · {apiData.pipeline.processing_time_s?.toFixed(1)}s
                        </span>
                        <span className="text-[10px] font-bold px-2 py-1 rounded-full" style={{ backgroundColor: '#8b5cf615', color: '#8b5cf6' }}>
                          {apiData.pipeline.max_frames || apiData.pipeline.total_frames} frames
                        </span>
                        {apiData.pipeline?.race_window && (
                          <span className="text-[10px] font-bold px-2 py-1 rounded-full" style={{ backgroundColor: '#06b6d415', color: '#06b6d4' }}>
                            🎯 Janela: {apiData.pipeline.race_window.race_duration_s?.toFixed(1)}s ({apiData.pipeline.race_window.efficiency_pct}% do vídeo)
                          </span>
                        )}
                      </div>
                    )}

                    {/* ── Competition Summary (quantitative only) ── */}
                    {isCompMode && (() => {
                      const cs = apiData?.competition_summary || _buildCompSummaryFromBio(apiData);
                      if (!cs) return null;
                      return (
                        <>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                            <MetricCard data={{ label: 'Braçadas', value: apiData?.canonicalStrokeCount || cs.stroke_count_total, unit: '', icon: Waves, trend: 'neutral' }} />
                            <MetricCard data={{ label: 'Freq. Braçada', value: cs.stroke_rate_avg?.toFixed(1), unit: '/min', icon: Activity, trend: 'neutral' }} />
                            <MetricCard data={{ label: 'DPS', value: cs.dps_m?.toFixed(2), unit: 'm', icon: Ruler, trend: 'neutral' }} />
                            <MetricCard data={{ label: 'Braç/Metro', value: cs.strokes_per_meter?.toFixed(2), unit: '', icon: BarChart3, trend: 'neutral' }} />
                          </div>

                          {/* Splits table (competition) */}
                          {cs.splits?.length > 0 && (
                            <div className="mb-4 rounded-xl overflow-hidden" style={{ border: `1px solid ${VUKZ.border}` }}>
                              <div className="px-3 py-2" style={{ backgroundColor: VUKZ.primary + '08' }}>
                                <span className="text-[10px] font-bold uppercase" style={{ color: VUKZ.textLight }}>Parciais</span>
                                {cs.pacing_type && cs.pacing_type !== 'unknown' && (
                                  <span className="ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full" style={{
                                    backgroundColor: cs.pacing_type === 'negative_split' ? '#10b98115' : cs.pacing_type === 'positive_split' ? '#ef444415' : '#6366f115',
                                    color: cs.pacing_type === 'negative_split' ? '#10b981' : cs.pacing_type === 'positive_split' ? '#ef4444' : '#6366f1',
                                  }}>
                                    {cs.pacing_type === 'negative_split' ? '🚀 Negative Split' : cs.pacing_type === 'positive_split' ? '📉 Positive Split' : '⚖️ Even'}
                                  </span>
                                )}
                              </div>
                              <table className="w-full text-xs">
                                <thead>
                                  <tr style={{ color: VUKZ.textLight }}>
                                    <th className="text-left px-3 py-2 font-semibold">Parcial</th>
                                    <th className="text-right px-3 py-2 font-semibold">Tempo</th>
                                    <th className="text-right px-3 py-2 font-semibold">Acum.</th>
                                    <th className="text-right px-3 py-2 font-semibold">Braçadas</th>
                                    <th className="text-right px-3 py-2 font-semibold">Freq</th>
                                    <th className="text-right px-3 py-2 font-semibold">Braç/m</th>
                                    <th className="text-right px-3 py-2 font-semibold">Vel</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {cs.splits.map((sp, i) => (
                                    <tr key={i} className="border-t" style={{ borderColor: VUKZ.border }}>
                                      <td className="px-3 py-2 font-bold" style={{ color: VUKZ.text }}>{sp.label}</td>
                                      <td className="px-3 py-2 text-right font-black" style={{ color: VUKZ.primary }}>{sp.time_s?.toFixed(2)}s</td>
                                      <td className="px-3 py-2 text-right" style={{ color: VUKZ.textLight }}>{sp.cumulative_s?.toFixed(2)}s</td>
                                      <td className="px-3 py-2 text-right font-bold" style={{ color: VUKZ.text }}>{sp.strokes}</td>
                                      <td className="px-3 py-2 text-right" style={{ color: VUKZ.textLight }}>{sp.stroke_rate?.toFixed(1)}</td>
                                      <td className="px-3 py-2 text-right" style={{ color: VUKZ.textLight }}>{sp.strokes_per_meter?.toFixed(2)}</td>
                                      <td className="px-3 py-2 text-right" style={{ color: VUKZ.textLight }}>{sp.avg_velocity_ms?.toFixed(2)} m/s</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}

                          {/* Turns (competition) */}
                          {cs.turns?.length > 0 && (
                            <div className="mb-4 p-3 rounded-xl" style={{ backgroundColor: '#faf5ff' }}>
                              <div className="flex items-center gap-2 mb-2">
                                <RotateCcw size={14} style={{ color: '#8b5cf6' }} />
                                <span className="text-xs font-black" style={{ color: VUKZ.text }}>Viradas: {cs.turns.length}</span>
                              </div>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                {cs.turns.map((t) => (
                                  <div key={t.turn_number} className="p-2 rounded-lg bg-white border" style={{ borderColor: VUKZ.border }}>
                                    <div className="text-[10px] font-bold" style={{ color: VUKZ.textLight }}>Virada {t.turn_number} ({t.at_distance_m}m)</div>
                                    <div className="text-sm font-black" style={{ color: VUKZ.text }}>{t.time_s?.toFixed(2)}s</div>
                                    {t.wall_time_s > 0 && (
                                      <div className="text-[9px]" style={{ color: VUKZ.textLight }}>Parede: {t.wall_time_s?.toFixed(2)}s</div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Warnings */}
                          {cs.warnings?.length > 0 && (
                            <div className="p-3 rounded-xl" style={{ backgroundColor: '#fef2f2' }}>
                              {cs.warnings.map((w, i) => (
                                <p key={i} className="text-xs flex items-center gap-1" style={{ color: '#ef4444' }}>
                                  <AlertTriangle size={12} /> {w}
                                </p>
                              ))}
                            </div>
                          )}

                          {/* ── CRONOMETRAGEM (Timing from audio start signal) ── */}
                          {(() => {
                            const timing = cs.timing || apiData?.timing;
                            if (!timing || !timing.start_signal_detected) return null;
                            return (
                              <div className="mb-4 rounded-xl overflow-hidden" style={{ border: `1px solid ${VUKZ.border}` }}>
                                <div className="px-3 py-2 flex items-center gap-2" style={{ backgroundColor: '#f0fdf4' }}>
                                  <Clock size={14} style={{ color: '#059669' }} />
                                  <span className="text-[10px] font-bold uppercase" style={{ color: '#059669' }}>Cronometragem</span>
                                  <span className="ml-auto text-[9px] px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: '#05966915', color: '#059669' }}>
                                    🔊 Tiro detectado
                                  </span>
                                </div>

                                {/* Key timing metrics */}
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3">
                                  {timing.reaction_time_s > 0 && (
                                    <div className="p-2 rounded-lg bg-white border text-center" style={{ borderColor: VUKZ.border }}>
                                      <div className="text-[9px] font-bold uppercase" style={{ color: VUKZ.textLight }}>Tempo de Reação</div>
                                      <div className="text-lg font-black" style={{ color: timing.reaction_time_s < 0.7 ? '#059669' : timing.reaction_time_s < 1.0 ? '#f59e0b' : '#ef4444' }}>
                                        {timing.reaction_time_s.toFixed(3)}s
                                      </div>
                                    </div>
                                  )}
                                  {timing.underwater_time_s > 0 && (
                                    <div className="p-2 rounded-lg bg-white border text-center" style={{ borderColor: VUKZ.border }}>
                                      <div className="text-[9px] font-bold uppercase" style={{ color: VUKZ.textLight }}>Submerso (Saída)</div>
                                      <div className="text-lg font-black" style={{ color: VUKZ.primary }}>
                                        {timing.underwater_time_s.toFixed(2)}s
                                      </div>
                                      {timing.breakout_distance_m > 0 && (
                                        <div className="text-[9px]" style={{ color: VUKZ.textLight }}>Breakout: {timing.breakout_distance_m.toFixed(0)}m</div>
                                      )}
                                    </div>
                                  )}
                                  {timing.total_time_s > 0 && (
                                    <div className="p-2 rounded-lg bg-white border text-center" style={{ borderColor: VUKZ.border }}>
                                      <div className="text-[9px] font-bold uppercase" style={{ color: VUKZ.textLight }}>Tempo Total</div>
                                      <div className="text-lg font-black" style={{ color: VUKZ.primary }}>
                                        {timing.total_time_s.toFixed(2)}s
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Distance splits table */}
                                {timing.distance_splits?.length > 0 && (
                                  <div className="px-3 pb-3">
                                    <div className="text-[10px] font-bold uppercase mb-2" style={{ color: VUKZ.textLight }}>
                                      Parciais por Distância
                                    </div>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                      {timing.distance_splits.map((sp, i) => (
                                        <div key={i} className="p-2 rounded-lg border text-center" style={{
                                          borderColor: VUKZ.border,
                                          backgroundColor: i === 0 ? '#f0fdf4' : '#fff',
                                        }}>
                                          <div className="text-xs font-black" style={{ color: VUKZ.text }}>
                                            {sp.distance_m}m
                                          </div>
                                          <div className="text-sm font-black" style={{ color: VUKZ.primary }}>
                                            {sp.time_s?.toFixed(2)}s
                                          </div>
                                          {sp.speed_ms && (
                                            <div className="text-[9px]" style={{ color: VUKZ.textLight }}>
                                              {sp.speed_ms.toFixed(2)} m/s
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Lap splits for multi-lap races */}
                                {timing.lap_splits?.length > 0 && (
                                  <div className="px-3 pb-3">
                                    <div className="text-[10px] font-bold uppercase mb-2" style={{ color: VUKZ.textLight }}>
                                      Parciais por 50m
                                    </div>
                                    <table className="w-full text-xs">
                                      <thead>
                                        <tr style={{ color: VUKZ.textLight }}>
                                          <th className="text-left px-2 py-1 font-semibold">Lap</th>
                                          {timing.lap_splits.some(l => l.style) && (
                                            <th className="text-left px-2 py-1 font-semibold">Estilo</th>
                                          )}
                                          <th className="text-right px-2 py-1 font-semibold">Tempo</th>
                                          <th className="text-right px-2 py-1 font-semibold">Acum.</th>
                                          <th className="text-right px-2 py-1 font-semibold">Braçadas</th>
                                          <th className="text-right px-2 py-1 font-semibold">Freq</th>
                                          <th className="text-right px-2 py-1 font-semibold">Vel</th>
                                          <th className="text-right px-2 py-1 font-semibold">Submerso</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {timing.lap_splits.map((lap, i) => (
                                          <tr key={i} className="border-t" style={{ borderColor: VUKZ.border }}>
                                            <td className="px-2 py-1 font-bold" style={{ color: VUKZ.text }}>
                                              {lap.distance_m}m
                                            </td>
                                            {timing.lap_splits.some(l => l.style) && (
                                              <td className="px-2 py-1 text-xs" style={{ color: VUKZ.primary }}>
                                                {({'freestyle':'Livre','backstroke':'Costas','butterfly':'Borboleta','breaststroke':'Peito','medley':'Medley'})[lap.style] || lap.style || '—'}
                                              </td>
                                            )}
                                            <td className="px-2 py-1 text-right font-black" style={{ color: VUKZ.primary }}>
                                              {lap.lap_time_s?.toFixed(2)}s
                                            </td>
                                            <td className="px-2 py-1 text-right" style={{ color: VUKZ.textLight }}>
                                              {lap.cumulative_s?.toFixed(2)}s
                                            </td>
                                            <td className="px-2 py-1 text-right font-bold" style={{ color: VUKZ.text }}>
                                              {lap.strokes}
                                            </td>
                                            <td className="px-2 py-1 text-right" style={{ color: VUKZ.textLight }}>
                                              {lap.stroke_rate?.toFixed(1)}
                                            </td>
                                            <td className="px-2 py-1 text-right" style={{ color: VUKZ.textLight }}>
                                              {lap.avg_speed_ms?.toFixed(2)} m/s
                                            </td>
                                            <td className="px-2 py-1 text-right" style={{ color: VUKZ.textLight }}>
                                              {lap.underwater_s?.toFixed(2)}s
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}

                                {/* Per-phase underwater details */}
                                {timing.underwater_phases?.length > 1 && (
                                  <div className="px-3 pb-3">
                                    <div className="text-[10px] font-bold uppercase mb-2" style={{ color: VUKZ.textLight }}>
                                      Tempo Submerso por Fase
                                    </div>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                      {timing.underwater_phases.map((uw, i) => (
                                        <div key={i} className="p-2 rounded-lg border text-center" style={{ borderColor: VUKZ.border }}>
                                          <div className="text-[9px] font-bold" style={{ color: VUKZ.textLight }}>
                                            {uw.type === 'dive' ? '🏊 Saída' : `🔄 Virada ${uw.type.replace('turn_', '')}`}
                                          </div>
                                          <div className="text-sm font-black" style={{ color: VUKZ.primary }}>
                                            {uw.duration_s?.toFixed(2)}s
                                          </div>
                                          {uw.breakout_distance_m > 0 && (
                                            <div className="text-[9px]" style={{ color: VUKZ.textLight }}>
                                              {uw.breakout_distance_m.toFixed(0)}m
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Timing warnings */}
                                {timing.warnings?.length > 0 && (
                                  <div className="px-3 pb-3">
                                    {timing.warnings.map((w, i) => (
                                      <p key={i} className="text-[10px] flex items-center gap-1" style={{ color: '#f59e0b' }}>
                                        <AlertTriangle size={10} /> {w}
                                      </p>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </>
                      );
                    })()}

                    {/* ── Training Mode Metrics (full biomechanics) ── */}
                    {!isCompMode && metrics?.stroke && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {/* Row 1: Core stroke metrics */}
                        <div className="relative">
                          <MetricCard data={{ label: 'Braçadas', value: apiData?.canonicalStrokeCount || metrics.stroke.count, unit: '', icon: Waves, trend: 'neutral' }} />
                          {metrics.stroke.counting_confidence > 0 && (
                            <div className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded text-[8px] font-black" style={{ backgroundColor: metrics.stroke.counting_confidence >= 80 ? '#10b98118' : '#f59e0b18', color: metrics.stroke.counting_confidence >= 80 ? '#10b981' : '#f59e0b' }}>
                              {metrics.stroke.counting_confidence.toFixed(0)}% conf.
                            </div>
                          )}
                        </div>
                        <MetricCard data={{ label: 'Freq. Braçada', value: metrics.stroke.rate_per_min?.toFixed(1), unit: '/min', icon: Activity, trend: 'neutral' }} />
                        <MetricCard data={{ label: 'Ciclo Médio', value: metrics.stroke.cycle_time_avg?.toFixed(2), unit: 's', icon: Timer, trend: 'neutral' }} />
                        <MetricCard data={{ label: 'Simetria L/R', value: ((metrics.stroke.left_right_ratio || 0) * 100).toFixed(0), unit: '%', icon: Target, trend: metrics.stroke.left_right_ratio >= 0.9 ? 'up' : 'down' }} />
                        {/* Row 2: Score + distance metrics */}
                        <MetricCard data={{ label: 'Score Geral', value: metrics.overall_score?.toFixed(0), unit: '/100', icon: Gauge, trend: metrics.overall_score >= 75 ? 'up' : 'down' }} />
                        {metrics.stroke.stroke_length_m > 0 && (
                          <MetricCard data={{ label: 'Dist./Braçada', value: metrics.stroke.stroke_length_m?.toFixed(2), unit: 'm', icon: Ruler, trend: metrics.stroke.stroke_length_m >= 1.8 ? 'up' : 'down' }} />
                        )}
                        {metrics.efficiency_index != null && (
                          <MetricCard data={{ label: 'Eficiência', value: metrics.efficiency_index?.toFixed(2), unit: '', icon: Zap, trend: metrics.efficiency_index >= 3.0 ? 'up' : metrics.efficiency_index >= 2.0 ? 'neutral' : 'down' }} />
                        )}
                        <MetricCard data={{ label: 'Fadiga', value: metrics.stroke.fatigue_index?.toFixed(3), unit: '', icon: Flame, trend: metrics.stroke.fatigue_index > 0.05 ? 'down' : 'up' }} />
                        {/* Row 3: Arm counts */}
                        <MetricCard data={{ label: 'Braço Esq.', value: metrics.stroke.left_count, unit: '', icon: Waves, trend: 'neutral' }} />
                        <MetricCard data={{ label: 'Braço Dir.', value: metrics.stroke.right_count, unit: '', icon: Waves, trend: 'neutral' }} />
                      </div>
                    )}

                    {/* Turn detection metrics (training mode only — competition shows turns in summary above) */}
                    {!isCompMode && metrics?.turn_detection && metrics.turn_detection.turn_count > 0 && (
                      <div className="mt-4 p-3 rounded-xl" style={{ backgroundColor: '#faf5ff' }}>
                        <div className="flex items-center gap-2 mb-2">
                          <RotateCcw size={14} style={{ color: '#8b5cf6' }} />
                          <span className="text-xs font-black" style={{ color: VUKZ.text }}>Viradas Detectadas: {metrics.turn_detection.turn_count}</span>
                          <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: '#8b5cf615', color: '#8b5cf6' }}>
                            Score médio: {metrics.turn_detection.avg_quality_score?.toFixed(0)}/100
                          </span>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {metrics.turn_detection.turns.map((t) => (
                            <div key={t.turn_number} className="p-2 rounded-lg bg-white border" style={{ borderColor: VUKZ.border }}>
                              <div className="text-[10px] font-bold" style={{ color: VUKZ.textLight }}>Virada {t.turn_number}</div>
                              <div className="text-sm font-black" style={{ color: VUKZ.text }}>{t.wall_time_s?.toFixed(2)}s</div>
                              <div className="flex gap-1 mt-1">
                                <span className="text-[9px] px-1 py-0.5 rounded" style={{ backgroundColor: t.turn_type === 'flip' ? '#8b5cf615' : '#f59e0b15', color: t.turn_type === 'flip' ? '#8b5cf6' : '#f59e0b' }}>{t.turn_type}</span>
                                <span className="text-[9px] px-1 py-0.5 rounded" style={{ backgroundColor: t.quality_score >= 70 ? '#10b98115' : '#ef444415', color: t.quality_score >= 70 ? '#10b981' : '#ef4444' }}>{t.quality_score?.toFixed(0)}/100</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Technique scores (training mode only) */}
                    {!isCompMode && metrics?.scores?.length > 0 && (
                      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {metrics.scores.map((s) => (
                          <div key={s.category} className="p-3 rounded-xl border" style={{ borderColor: s.grade === 'A' ? '#10b98130' : s.grade === 'B' ? '#6366f130' : '#f59e0b30', backgroundColor: s.grade === 'A' ? '#10b98108' : s.grade === 'B' ? '#6366f108' : '#f59e0b08' }}>
                            <div className="flex justify-between items-center mb-1">
                              <span className="text-[10px] font-bold uppercase" style={{ color: VUKZ.textLight }}>{s.category.replace(/_/g, ' ')}</span>
                              <span className="text-xs font-black px-1.5 py-0.5 rounded" style={{ backgroundColor: s.grade === 'A' ? '#10b98120' : s.grade === 'B' ? '#6366f120' : '#f59e0b20', color: s.grade === 'A' ? '#10b981' : s.grade === 'B' ? '#6366f1' : '#f59e0b' }}>{s.grade}</span>
                            </div>
                            <div className="text-lg font-black" style={{ color: VUKZ.text }}>{s.score?.toFixed(0)}<span className="text-[10px] font-normal">/100</span></div>
                            <p className="text-[10px] mt-1" style={{ color: VUKZ.textLight }}>{s.feedback}</p>
                            {/* Drill recommendations (grades C, D, F) */}
                            {s.drills?.length > 0 && ['C', 'D', 'F'].includes(s.grade) && (
                              <div className="mt-2 pt-2 space-y-1" style={{ borderTop: `1px solid ${VUKZ.border}` }}>
                                <span className="text-[9px] font-bold uppercase" style={{ color: '#f59e0b' }}>Exercícios sugeridos</span>
                                {s.drills.slice(0, 3).map((drill, di) => (
                                  <div key={di} className="flex items-start gap-1.5">
                                    <span className="text-[9px] mt-0.5" style={{ color: '#f59e0b' }}>▸</span>
                                    <span className="text-[9px]" style={{ color: VUKZ.text }}>{drill}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'angles' && (
                  <div className="space-y-2">
                    {metrics?.angles ? Object.entries(metrics.angles).map(([name, a]) => {
                      const inRange = (a.in_range_pct || 0) >= 70;
                      const optMin = a.optimal_range?.[0];
                      const optMax = a.optimal_range?.[1];
                      return (
                        <AngleRow key={name} angle={{
                          joint: ANGLE_LABELS[name] || name.replace(/_/g, ' '),
                          value: a.mean?.toFixed(1),
                          ideal: optMin && optMax ? `${optMin}–${optMax}°` : '—',
                          status: inRange ? 'ok' : (a.in_range_pct || 0) >= 50 ? 'warning' : 'error',
                          detail: `Média: ${a.mean?.toFixed(1)}° ± ${a.std?.toFixed(1)}° | Range: ${a.min?.toFixed(0)}–${a.max?.toFixed(0)}° | ${(a.in_range_pct || 0).toFixed(0)}% no range ideal | Tendência: ${a.trend > 0 ? '↑' : a.trend < 0 ? '↓' : '→'}`,
                        }} />
                      );
                    }) : <p className="text-xs" style={{ color: VUKZ.textLight }}>Sem dados de ângulos. Execute a análise.</p>}
                  </div>
                )}

                {activeTab === 'splits' && (
                  <div className="overflow-x-auto">
                    {metrics?.split_analysis?.splits?.length > 0 ? (
                      <>
                        {/* Pacing badge */}
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-[10px] font-bold uppercase px-2 py-1 rounded-full" style={{
                            backgroundColor: metrics.split_analysis.pacing_type === 'negative' ? '#10b98115' : metrics.split_analysis.pacing_type === 'positive' ? '#ef444415' : '#6366f115',
                            color: metrics.split_analysis.pacing_type === 'negative' ? '#10b981' : metrics.split_analysis.pacing_type === 'positive' ? '#ef4444' : '#6366f1',
                          }}>
                            {metrics.split_analysis.pacing_type === 'negative' ? '🚀 Negative Split' : metrics.split_analysis.pacing_type === 'positive' ? '📉 Positive Split' : '⚖️ Even Split'}
                          </span>
                          <span className="text-[10px]" style={{ color: VUKZ.textLight }}>
                            Total: {metrics.split_analysis.total_time_s?.toFixed(2)}s | Δ metades: {metrics.split_analysis.half_delta_s > 0 ? '+' : ''}{metrics.split_analysis.half_delta_s?.toFixed(2)}s
                          </span>
                        </div>
                        <table className="w-full text-xs">
                          <thead>
                            <tr style={{ color: VUKZ.textLight }}>
                              <th className="text-left py-2 font-semibold">Parcial</th>
                              <th className="text-right py-2 font-semibold">Tempo</th>
                              <th className="text-right py-2 font-semibold">Acumulado</th>
                              <th className="text-right py-2 font-semibold">Pace/100m</th>
                              <th className="text-right py-2 font-semibold">Vel (m/s)</th>
                              <th className="text-center py-2 font-semibold">Δ</th>
                            </tr>
                          </thead>
                          <tbody>
                            {metrics.split_analysis.splits.map((s) => (
                              <tr key={s.split_number} className="border-t" style={{ borderColor: VUKZ.border, backgroundColor: s.is_fastest ? '#10b98108' : s.is_slowest ? '#ef444408' : 'transparent' }}>
                                <td className="py-2.5 font-bold" style={{ color: VUKZ.text }}>
                                  {s.distance_label}
                                  {s.is_fastest && <span className="ml-1 text-[9px]">🏆</span>}
                                  {s.is_slowest && <span className="ml-1 text-[9px]">⚠️</span>}
                                </td>
                                <td className="py-2.5 text-right font-black" style={{ color: VUKZ.primary }}>{s.split_time_s?.toFixed(2)}s</td>
                                <td className="py-2.5 text-right font-semibold" style={{ color: VUKZ.textLight }}>{s.cumulative_time_s?.toFixed(2)}s</td>
                                <td className="py-2.5 text-right font-semibold" style={{ color: VUKZ.textLight }}>{s.pace_per_100m_s?.toFixed(2)}s</td>
                                <td className="py-2.5 text-right font-semibold" style={{ color: VUKZ.textLight }}>{s.avg_velocity_ms?.toFixed(2)}</td>
                                <td className="py-2.5 text-center font-semibold" style={{ color: s.delta_vs_prev_s > 0.5 ? '#ef4444' : s.delta_vs_prev_s < -0.5 ? '#10b981' : VUKZ.textLight }}>
                                  {s.split_number > 1 ? (s.delta_vs_prev_s > 0 ? '+' : '') + s.delta_vs_prev_s?.toFixed(2) + 's' : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    ) : (
                      <p className="text-xs py-4 text-center" style={{ color: VUKZ.textLight }}>
                        {metrics?.stroke ? 'Prova de 50m (sem viradas). Parciais disponíveis para 100m+.' : 'Execute a análise para ver as parciais.'}
                      </p>
                    )}

                    {/* Legacy table removed — splits now use real pipeline data above */}
                  </div>
                )}

                {activeTab === 'events' && (
                  <EventsTab
                    metrics={metrics}
                    apiData={apiData}
                    debugOverlay={debugOverlay}
                    debugLoading={debugLoading}
                    overlayMode={overlayMode}
                    onOverlayModeChange={setOverlayMode}
                    onGenerateDebug={async () => {
                      setDebugLoading(true);
                      try {
                        // Try backend first if session_id exists
                        if (apiData?.session_id) {
                          const resp = await generateDebugOverlay(apiData.session_id, { lane: selectedLanes?.[0] || 1, overlay_mode: overlayMode });
                          if (resp?.success && resp.data) {
                            setDebugOverlay(resp.data);
                            return;
                          }
                        }
                        // Fallback: build debug data locally from pipeline response
                        const localDebug = _buildLocalDebugOverlay(apiData);
                        if (localDebug) setDebugOverlay(localDebug);
                      } catch (e) {
                        console.error('Debug overlay error:', e);
                        // Still try local fallback on error
                        const localDebug = _buildLocalDebugOverlay(apiData);
                        if (localDebug) setDebugOverlay(localDebug);
                      } finally {
                        setDebugLoading(false);
                      }
                    }}
                  />
                )}

                {activeTab === 'timing' && (
                  <StrokeTimingTab
                    strokeChartUrl={apiData?.strokeChartUrl}
                    strokeTiming={apiData?.strokeTiming}
                    startToFirstStrokeSeconds={apiData?.startToFirstStrokeS}
                    largestGapSeconds={apiData?.largestGapS}
                    raceDurationSeconds={apiData?.raceDurationSeconds}
                    reactionTimeOfficial={apiData?.reactionTimeOfficial}
                    totalTimeOfficial={apiData?.totalTimeOfficial}
                    splitsOfficial={apiData?.splitsOfficial}
                    officialResultId={apiData?.officialResultId}
                    sessionId={apiData?.session_id}
                  />
                )}

                {activeTab === 'dashboard' && (
                  <RaceDashboardTab
                    apiData={apiData}
                    sessionId={apiData?.session_id}
                  />
                )}

                {activeTab === 'insights' && (
                  <div className="space-y-4">
                    {/* Technique scores insight */}
                    {metrics?.scores?.length > 0 && (
                      <div>
                        <h4 className="text-[10px] font-bold uppercase tracking-wide mb-2" style={{ color: VUKZ.textLight }}>
                          Scores de Técnica (dados reais)
                        </h4>
                        <div className="space-y-2">
                          {metrics.scores.map((s) => (
                            <div key={s.category} className="p-3 rounded-xl border" style={{ borderColor: s.grade === 'A' ? '#10b98130' : s.grade === 'B' ? '#6366f130' : '#f59e0b30', backgroundColor: s.grade === 'A' ? '#10b98105' : s.grade === 'B' ? '#6366f105' : '#f59e0b05' }}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-xs font-black" style={{ color: VUKZ.text }}>{s.category.replace(/_/g, ' ')}</span>
                                <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: s.grade === 'A' ? '#10b98115' : s.grade === 'B' ? '#6366f115' : '#f59e0b15', color: s.grade === 'A' ? '#10b981' : s.grade === 'B' ? '#6366f1' : '#f59e0b' }}>
                                  {s.score?.toFixed(0)}/100 ({s.grade})
                                </span>
                              </div>
                              <p className="text-[11px] leading-relaxed" style={{ color: VUKZ.textLight }}>{s.feedback}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Recommendations from pipeline */}
                    {metrics?.recommendations?.length > 0 && (
                      <div>
                        <h4 className="text-[10px] font-bold uppercase tracking-wide mb-2" style={{ color: VUKZ.textLight }}>
                          Recomendações da IA
                        </h4>
                        <div className="space-y-1.5">
                          {metrics.recommendations.map((rec, i) => (
                            <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg" style={{ backgroundColor: rec.priority === 'high' ? '#fef2f2' : rec.priority === 'medium' ? '#fffbeb' : VUKZ.primaryLight }}>
                              <ChevronRight size={12} style={{ color: rec.priority === 'high' ? '#ef4444' : rec.priority === 'medium' ? '#f59e0b' : VUKZ.textLight }} className="mt-0.5 flex-shrink-0" />
                              <div>
                                <span className="text-[10px] font-bold" style={{ color: VUKZ.text }}>{rec.area}</span>
                                <p className="text-xs leading-relaxed" style={{ color: VUKZ.textLight }}>{rec.suggestion}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Pipeline info — REAL data */}
                    {apiData?.pipeline && (
                      <div className="p-3 rounded-lg border" style={{ borderColor: VUKZ.border }}>
                        <h4 className="text-[10px] font-bold uppercase tracking-wide mb-2" style={{ color: VUKZ.textLight }}>
                          Pipeline Info (dados reais)
                        </h4>
                        <div className="grid grid-cols-2 gap-2 text-[11px]">
                          <div><span style={{ color: VUKZ.textLight }}>Modelo:</span> <span className="font-bold" style={{ color: VUKZ.text }}>{apiData.pipeline.model_version}</span></div>
                          <div><span style={{ color: VUKZ.textLight }}>Device:</span> <span className="font-bold" style={{ color: VUKZ.text }}>{apiData.pipeline.device}</span></div>
                          <div><span style={{ color: VUKZ.textLight }}>Tempo:</span> <span className="font-bold" style={{ color: VUKZ.text }}>{apiData.pipeline.processing_time_s?.toFixed(1)}s</span></div>
                          <div><span style={{ color: VUKZ.textLight }}>Frames:</span> <span className="font-bold" style={{ color: VUKZ.text }}>{apiData.pipeline.max_frames}</span></div>
                          <div><span style={{ color: VUKZ.textLight }}>Stride:</span> <span className="font-bold" style={{ color: VUKZ.text }}>{apiData.pipeline.frame_stride}</span></div>
                          <div><span style={{ color: VUKZ.textLight }}>Fonte:</span> <span className="font-bold" style={{ color: '#10b981' }}>{apiData.pipeline.data_source === 'real' ? '✓ REAL' : apiData.pipeline.data_source}</span></div>
                        </div>
                      </div>
                    )}

                    {/* Fallback when no data */}
                    {!metrics && (
                      <div className="text-center py-6">
                        <Brain size={24} style={{ color: VUKZ.textLight }} className="mx-auto mb-2" />
                        <p className="text-xs" style={{ color: VUKZ.textLight }}>Execute a análise para ver insights do pipeline SwimXYZ.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            );
          })()}
        </div>

        {/* ═════ RIGHT: AI Chat ═════ */}
        <div className={`lg:pl-4 flex flex-col mt-4 lg:mt-0 transition-all duration-300 ${expandedVideo ? 'hidden' : 'lg:w-[45%]'}`} style={{ minHeight: '600px' }}>
          <div className="bg-white rounded-2xl border flex-1 flex flex-col overflow-hidden" style={{ borderColor: VUKZ.border }}>

            {/* Chat header */}
            <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: VUKZ.border }}>
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: VUKZ_GRADIENT }}>
                  <Brain size={16} color="white" />
                </div>
                <div>
                  <h3 className="text-sm font-black" style={{ color: VUKZ.text }}>Swim Vision AI</h3>
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: VUKZ.success }} />
                    <span className="text-[10px]" style={{ color: VUKZ.success }}>Online · Pronto para análise</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-all">
                  <Settings size={14} style={{ color: VUKZ.textLight }} />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4">
              {messages.map((msg, idx) => (
                <ChatBubble key={idx} msg={msg} onFeedback={(positive) => console.log(`Feedback ${idx}: ${positive}`)} />
              ))}
              <div ref={chatEndRef} />
            </div>

            {/* Quick prompts */}
            {metrics && (
              <div className="px-4 pb-2">
                <div className="flex flex-wrap gap-1.5">
                  {QUICK_PROMPTS.map((qp) => {
                    const QIcon = qp.icon;
                    return (
                      <button
                        key={qp.label}
                        onClick={() => handleQuickPrompt(qp.prompt)}
                        className="flex items-center gap-1.5 text-[10px] font-bold px-3 py-1.5 rounded-lg transition-all hover:shadow-sm"
                        style={{ backgroundColor: VUKZ.primaryLight, color: VUKZ.text }}
                      >
                        <QIcon size={10} /> {qp.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Input */}
            <div className="px-4 py-3 border-t" style={{ borderColor: VUKZ.border }}>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={inputMsg}
                  onChange={(e) => setInputMsg(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder="Pergunte sobre a técnica, braçadas, viradas..."
                  className="flex-1 px-4 py-2.5 rounded-xl text-sm focus:outline-none transition-all"
                  style={{ backgroundColor: VUKZ.primaryLight, color: VUKZ.text, border: `1px solid ${VUKZ.border}` }}
                />
                <button
                  onClick={sendMessage}
                  disabled={!inputMsg.trim()}
                  className="w-10 h-10 rounded-xl flex items-center justify-center disabled:opacity-30 transition-all hover:scale-105"
                  style={{ background: VUKZ_GRADIENT }}
                >
                  <Send size={16} color="white" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* Link Race Modal                                            */}
      {/* ═══════════════════════════════════════════════════════════ */}
      <LinkRaceModal
        open={showLinkRaceModal}
        onClose={() => setShowLinkRaceModal(false)}
        onSave={(link) => setRaceLink(link)}
      />

      {/* ═══════════════════════════════════════════════════════════ */}
      {/* Digital Twin Modal                                         */}
      {/* ═══════════════════════════════════════════════════════════ */}
      {dtModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden">
            {/* Modal header */}
            <div className="relative h-44 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #374151 0%, #1f2937 50%, #111827 100%)' }}>
              <button onClick={() => setDtModalOpen(false)} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-all">
                <X size={16} color="white" />
              </button>
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-3 rounded-2xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                  <Box size={40} color="white" />
                </div>
                <h2 className="text-xl font-black text-white">Digital Twin 3D</h2>
                <p className="text-sm text-white/70 mt-1">Reconstrução biomecânica tridimensional</p>
              </div>
            </div>

            {/* Modal body */}
            <div className="p-6">
              <div className="space-y-4 mb-6">
                {[
                  { icon: Eye, title: 'Pose Estimation 3D', desc: 'Mapeamento de 33 keypoints corporais em espaço 3D a partir do vídeo 2D.' },
                  { icon: Activity, title: 'Modelo Biomecânico', desc: 'Cadeia cinemática completa com ângulos articulares dinâmicos frame a frame.' },
                  { icon: Sparkles, title: 'Visualização Interativa', desc: 'Modelo 3D rotacionável, com timeline de reprodução sincronizada ao vídeo.' },
                  { icon: TrendingUp, title: 'Análise Comparativa', desc: 'Sobreponha seu Digital Twin ao modelo ideal para identificar desvios.' },
                ].map((f) => {
                  const FIcon = f.icon;
                  return (
                    <div key={f.title} className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #37415115, #1f293715)' }}>
                        <FIcon size={18} style={{ color: '#374151' }} />
                      </div>
                      <div>
                        <h4 className="text-sm font-bold" style={{ color: VUKZ.text }}>{f.title}</h4>
                        <p className="text-[11px] leading-relaxed" style={{ color: VUKZ.textLight }}>{f.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Status */}
              <div className="p-4 rounded-xl mb-4" style={{ backgroundColor: '#fefce8' }}>
                <div className="flex items-center gap-2 mb-1">
                  <Flame size={14} style={{ color: '#ca8a04' }} />
                  <span className="text-xs font-bold" style={{ color: '#92400e' }}>Em desenvolvimento</span>
                </div>
                <p className="text-[11px] leading-relaxed" style={{ color: '#a16207' }}>
                  O módulo Digital Twin 3D está em fase de desenvolvimento ativo. Será integrado com MediaPipe Pose + Three.js para reconstrução em tempo real.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setDtModalOpen(false)}
                  className="flex-1 py-3 rounded-xl font-bold text-sm transition-all"
                  style={{ backgroundColor: VUKZ.primaryLight, color: VUKZ.text }}
                >
                  Fechar
                </button>
                <button
                  className="flex-1 py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all hover:scale-105"
                  style={{ background: 'linear-gradient(135deg, #374151 0%, #111827 100%)', color: 'white' }}
                >
                  <Sparkles size={14} /> Notificar quando disponível
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
